/**
 * Mock data provider for the tutorial sandbox (demo mode).
 *
 * Maps API URL patterns to static GET responses so that tutorials
 * can run without hitting the real backend. Each entry uses a regex
 * to match the request URL and returns mock data when matched.
 *
 * The dataset uses realistic French agro-food industry data with
 * stable UUIDs so that cross-entity references remain consistent.
 *
 * Add new entries here as tutorials cover more features.
 */

export interface MockRouteEntry {
  /** Regex applied against the request URL pathname */
  pattern: RegExp;
  /** The JSON body to return (or a factory that receives regex match and progress) */
  data: unknown | ((match: RegExpMatchArray, progress: number) => unknown);
}

// ---------------------------------------------------------------------------
// Stable UUIDs
// ---------------------------------------------------------------------------

// Articles
const ART_FARINE    = 'a1b2c3d4-e5f6-4a7b-8c9d-0e1f2a3b4c5d';
const ART_BEURRE    = 'b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e';
const ART_SUCRE     = 'c3d4e5f6-a7b8-4c9d-0e1f-2a3b4c5d6e7f';
const ART_OEUFS     = 'd4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a';
const ART_LAIT      = 'e5f6a7b8-c9d0-4e1f-2a3b-4c5d6e7f8a9b';
const ART_BISCUITS  = 'f6a7b8c9-d0e1-4f2a-3b4c-000000000001';

// ArticleInfos (1:1 with Article)
const AI_FARINE     = 'f6a7b8c9-d0e1-4f2a-3b4c-5d6e7f8a9b0c';
const AI_BEURRE     = 'a7b8c9d0-e1f2-4a3b-4c5d-6e7f8a9b0c1d';
const AI_SUCRE      = 'b8c9d0e1-f2a3-4b4c-5d6e-7f8a9b0c1d2e';
const AI_OEUFS      = 'c9d0e1f2-a3b4-4c5d-6e7f-8a9b0c1d2e3f';
const AI_LAIT       = 'd0e1f2a3-b4c5-4d6e-7f8a-9b0c1d2e3f4a';
const AI_BISCUITS   = 'e1f2a3b4-c5d6-4e7f-8a9b-000000000001';

// Tiers
const TIERS_MOULIN  = '11111111-aaaa-4bbb-cccc-dddddddddddd';
const TIERS_BOULANG = '22222222-aaaa-4bbb-cccc-dddddddddddd';
const TIERS_SUPER   = '33333333-aaaa-4bbb-cccc-dddddddddddd';

// Adresses
const ADR_MOULIN    = '44444444-aaaa-4bbb-cccc-dddddddddddd';
const ADR_BOULANG   = '55555555-aaaa-4bbb-cccc-dddddddddddd';
const ADR_SUPER     = '66666666-aaaa-4bbb-cccc-dddddddddddd';

// Lots
const LOT_FARINE    = '77777777-aaaa-4bbb-cccc-dddddddddddd';
const LOT_BEURRE    = '88888888-aaaa-4bbb-cccc-dddddddddddd';
const LOT_SUCRE     = '99999999-aaaa-4bbb-cccc-dddddddddddd';
const LOT_OEUFS     = 'aaaaaaaa-aaaa-4bbb-cccc-dddddddddddd';
const LOT_LAIT      = 'bbbbbbbb-aaaa-4bbb-cccc-dddddddddddd';
const LOT_BISCUITS  = 'cccccccc-aaaa-4bbb-cccc-dddddddddddd';

// Commandes fournisseur
const CF_001        = 'cf000001-aaaa-4bbb-cccc-dddddddddddd';
const CF_002        = 'cf000002-aaaa-4bbb-cccc-dddddddddddd';
const CF_003        = 'cf000003-aaaa-4bbb-cccc-dddddddddddd';

// Commandes client
const CC_001        = 'cc000001-aaaa-4bbb-cccc-dddddddddddd';
const CC_002        = 'cc000002-aaaa-4bbb-cccc-dddddddddddd';

// Lignes commande fournisseur
const CFL_001       = 'cfl00001-aaaa-4bbb-cccc-dddddddddddd';
const CFL_002       = 'cfl00002-aaaa-4bbb-cccc-dddddddddddd';
const CFL_003       = 'cfl00003-aaaa-4bbb-cccc-dddddddddddd';

// Lignes commande client
const CCL_001       = 'ccl00001-aaaa-4bbb-cccc-dddddddddddd';
const CCL_002       = 'ccl00002-aaaa-4bbb-cccc-dddddddddddd';
const CCL_003       = 'ccl00003-aaaa-4bbb-cccc-dddddddddddd';
const CCL_004       = 'ccl00004-aaaa-4bbb-cccc-dddddddddddd';

// Nomenclature
const NOM_BISCUITS  = 'nom00001-aaaa-4bbb-cccc-dddddddddddd';
const NOM_L_FARINE  = 'noml0001-aaaa-4bbb-cccc-dddddddddddd';
const NOM_L_BEURRE  = 'noml0002-aaaa-4bbb-cccc-dddddddddddd';
const NOM_L_SUCRE   = 'noml0003-aaaa-4bbb-cccc-dddddddddddd';
const NOM_L_OEUFS   = 'noml0004-aaaa-4bbb-cccc-dddddddddddd';
const NOM_L_LAIT    = 'noml0005-aaaa-4bbb-cccc-dddddddddddd';

// Process etapes
const PE_MELANGE    = 'pe000001-aaaa-4bbb-cccc-dddddddddddd';
const PE_INCORP_B   = 'pe000002-aaaa-4bbb-cccc-dddddddddddd';
const PE_AJOUT_OL   = 'pe000003-aaaa-4bbb-cccc-dddddddddddd';
const PE_REPOS      = 'pe000004-aaaa-4bbb-cccc-dddddddddddd';
const PE_FACONNAGE  = 'pe000005-aaaa-4bbb-cccc-dddddddddddd';
const PE_CUISSON    = 'pe000006-aaaa-4bbb-cccc-dddddddddddd';
const PE_REFROID    = 'pe000007-aaaa-4bbb-cccc-dddddddddddd';
const PE_CONDITION  = 'pe000008-aaaa-4bbb-cccc-dddddddddddd';

// Controles qualite
const CTRL_TEMP     = 'ctrl0001-aaaa-4bbb-cccc-dddddddddddd';
const CTRL_ASPECT   = 'ctrl0002-aaaa-4bbb-cccc-dddddddddddd';
const CTRL_POIDS    = 'ctrl0003-aaaa-4bbb-cccc-dddddddddddd';
const CTRL_ETIQ     = 'ctrl0004-aaaa-4bbb-cccc-dddddddddddd';
const CTRL_HOMOG    = 'ctrl0005-aaaa-4bbb-cccc-dddddddddddd';
const CTRL_POD      = 'cpod0001-aaaa-4bbb-cccc-dddddddddddd';
const POD_MELANGE   = 'cpod0002-aaaa-4bbb-cccc-dddddddddddd';
const POD_CUISSON   = 'cpod0003-aaaa-4bbb-cccc-dddddddddddd';
const POD_CONDITION = 'cpod0004-aaaa-4bbb-cccc-dddddddddddd';

// Pods contrôles spécif (réception lait, début opé biscuits)
const POD_RECEP_LAIT = 'cpod0010-aaaa-4bbb-cccc-dddddddddddd';
const POD_STARTOPE_BISC = 'cpod0011-aaaa-4bbb-cccc-dddddddddddd';

// Ligne de production
const LIGNE_BISC    = 'lig00001-aaaa-4bbb-cccc-dddddddddddd';
const LIGNE_PARAM   = 'lgp00001-aaaa-4bbb-cccc-dddddddddddd';

// Ordre de fabrication
const OF_BISCUITS   = 'of000001-aaaa-4bbb-cccc-dddddddddddd';

// SpecificationEtape for Biscuits
const SE_BISCUITS   = 'se000001-aaaa-4bbb-cccc-dddddddddddd';

// SpecificationEtapeMaster IDs (1 per article)
const SEM_IDS = [
  'a0a0a0a0-0001-4000-a000-000000000001',
  'a0a0a0a0-0001-4000-a000-000000000002',
  'a0a0a0a0-0001-4000-a000-000000000003',
  'a0a0a0a0-0001-4000-a000-000000000004',
  'a0a0a0a0-0001-4000-a000-000000000005',
  'a0a0a0a0-0001-4000-a000-000000000006',
];

// CustomEnums
const ENUM_FAM_MP   = 'enu00001-aaaa-4bbb-cccc-dddddddddddd';
const ENUM_FAM_PF   = 'enu00002-aaaa-4bbb-cccc-dddddddddddd';
const ENUM_UNIT_KG  = 'enu00003-aaaa-4bbb-cccc-dddddddddddd';
const ENUM_UNIT_L   = 'enu00004-aaaa-4bbb-cccc-dddddddddddd';
const ENUM_UNIT_U   = 'enu00005-aaaa-4bbb-cccc-dddddddddddd';
const ENUM_FAM_TECH = 'enu00006-aaaa-4bbb-cccc-dddddddddddd';
const ENUM_SOUS_FAM_BISC = 'enu00007-aaaa-4bbb-cccc-dddddddddddd';

// ---------------------------------------------------------------------------
// Custom Enums
// ---------------------------------------------------------------------------

const customEnums = [
  {
    id: ENUM_FAM_MP,
    denomination: 'Matieres premieres',
    type: 'FAMILLE_ARTICLE',
    uniteRef: null,
    coefficient: 1,
    parentId: null,
    isActive: true,
  },
  {
    id: ENUM_FAM_PF,
    denomination: 'Produits finis',
    type: 'FAMILLE_ARTICLE',
    uniteRef: null,
    coefficient: 1,
    parentId: null,
    isActive: true,
    availableAt: 3.09,
  },
  {
    id: ENUM_UNIT_KG,
    denomination: 'Kilogramme',
    type: 'UNITE',
    uniteRef: 'KG',
    coefficient: 1,
    parentId: null,
    isActive: true,
    availableAt: 4,
  },
  {
    id: ENUM_UNIT_L,
    denomination: 'Litre',
    type: 'UNITE',
    uniteRef: 'L',
    coefficient: 1,
    parentId: null,
    isActive: true,
    availableAt: 4,
  },
  {
    id: ENUM_UNIT_U,
    denomination: 'Unite',
    type: 'UNITE',
    uniteRef: 'UNITE',
    coefficient: 1,
    parentId: null,
    isActive: true,
    availableAt: 4,
  },
  {
    id: ENUM_FAM_TECH,
    denomination: 'Biscuits',
    type: 'FAMILLE_TECHNIQUE',
    uniteRef: null,
    coefficient: 1,
    parentId: null,
    isActive: true,
    availableAt: 3.14,
  },
  {
    id: 'enu00007-aaaa-4bbb-cccc-dddddddddddd',
    denomination: 'Biscuits',
    type: 'SOUS_FAMILLE_ARTICLE',
    uniteRef: null,
    coefficient: 1,
    parentId: ENUM_FAM_PF,
    isActive: true,
    availableAt: 3.14,
  },
];

// ---------------------------------------------------------------------------
// ArticleInfos builder
// ---------------------------------------------------------------------------

function makeArticleInfos(overrides: Record<string, any>): any {
  return {
    id: overrides['id'],
    isActive: true,
    denomination: overrides['denomination'] ?? '',
    code: overrides['code'] ?? '',
    ean13: overrides['ean13'] ?? '',
    ean14: overrides['ean14'] ?? '',
    ean128: [],
    densite: overrides['densite'] ?? 0,
    bio: overrides['bio'] ?? false,
    versionSpecif: '',
    gross_weight_uc: 0,
    net_weight_uc: overrides['net_weight_uc'] ?? 0,
    longueur_uc: 0, hauteur_uc: 0, largeur_uc: 0,
    gross_weight_colis: 0, net_weight_colis: 0,
    longueur_colis: 0, hauteur_colis: 0, largeur_colis: 0,
    ingredients: overrides['ingredients'] ?? '',
    allergenes: overrides['allergenes'] ?? '',
    photo: '',
    infosPrixAchat: overrides['infosPrixAchat'] ?? [],
    infosPrixVente: overrides['infosPrixVente'] ?? [],
    dluoJours: overrides['dluoJours'] ?? 0,
    dluoMois: overrides['dluoMois'] ?? 0,
    dluoAnnes: overrides['dluoAnnes'] ?? 0,
    dluoFormat: null, typePeriode: null, ddmAsRef: false,
    usedBy: null,
    famille: overrides['famille'] ?? null,
    familleId: overrides['familleId'] ?? null,
    categorie: null, categorieId: null,
    sousFamille: overrides['sousFamille'] ?? null, sousFamilleId: overrides['sousFamilleId'] ?? null,
    unite: overrides['unite'] ?? null,
    uniteId: overrides['uniteId'] ?? null,
    withLot: overrides['withLot'] ?? true,
    withTraca: overrides['withTraca'] ?? false,
    withPiedDeChaine: overrides['withPiedDeChaine'] ?? false,
    withConsoTraca: overrides['withConsoTraca'] ?? false,
    withStockPositif: overrides['withStockPositif'] ?? false,
    withEmplacement: false, withSpecif: overrides['withSpecif'] ?? false, withParallelOpe: false,
    withStock: overrides['withStock'] ?? true,
    withValeursNutritionnelles: false,
    isPrestation: false, isAlimentaire: true,
    proprietaire: null, proprietaireId: null,
    pcb: overrides['pcb'] ?? 1,
    user: null, groupId: null, codeClient: '',
    familleCode: '', sousFamilleCode: '',
    uniteCode: overrides['uniteCode'] ?? '',
    uniteDeComptageCode: '',
    secable: false, laize: 0, epaisseur: 0, microbio: '',
    infoFicheTechnique: { label: '', decription: '' },
    weight_suremballage: 0, tailleEchantillon: 0,
    typeComptage: null, refCalcul: null, quantiteParDimension: 0,
    valeurUnitaire: 0, uniteDeComptage: null, coefficientComptage: 1,
    nbUnitePerPack: 0, diametreMandrin: 0, diametreComplet: 0,
    specificationEtapeId: null, specificationEtapeMasterId: null,
    specificationEtapeMaster: null,
    valeurNutritionnellesLigne: [], coefficientVN: 0, labelBisVN: '', mlVN: false,
    typeFiche: null,
    articleId: overrides['articleId'] ?? null,
    planifDisplayMode: 'flux',
    stockReappro: 0, quantiteOfDefault: 0, stockSecurite: 0,
    inventaireId: null,
  };
}

// ---------------------------------------------------------------------------
// ArticleInfos instances
// ---------------------------------------------------------------------------

const familleMP = customEnums.find(e => e.id === ENUM_FAM_MP)!;
const famillePF = customEnums.find(e => e.id === ENUM_FAM_PF)!;
const uniteKG   = customEnums.find(e => e.id === ENUM_UNIT_KG)!;
const uniteL    = customEnums.find(e => e.id === ENUM_UNIT_L)!;
const uniteU    = customEnums.find(e => e.id === ENUM_UNIT_U)!;

// Farine : PDC + Traça (ingrédient principal, géré au pied de chaîne)
const articleInfosFarine = makeArticleInfos({
  id: AI_FARINE, denomination: 'Farine de ble T65', code: 'MP-FAR-001',
  ean13: '3012345678901', bio: false,
  ingredients: 'Farine de ble (gluten)', allergenes: 'Gluten',
  famille: familleMP, familleId: ENUM_FAM_MP,
  unite: uniteKG, uniteId: ENUM_UNIT_KG, uniteCode: 'KG',
  withLot: true, withStock: true, withPiedDeChaine: true, withTraca: true, withStockPositif: true,
  net_weight_uc: 25, pcb: 1, articleId: ART_FARINE,
  infosPrixAchat: [{ quantiteMin: 0, prix: 0.85, isPrixAchat: true, articleFourniId: null }],
  infosPrixVente: [{ quantiteMin: 0, prix: 1.20, isPrixAchat: false, articleFourniId: null }],
});

// Beurre : Traça seulement (pas de PDC)
const articleInfosBeurre = makeArticleInfos({
  id: AI_BEURRE, denomination: 'Beurre doux 82%', code: 'MP-BEU-002',
  ean13: '3012345678918', bio: false,
  ingredients: 'Creme pasteurisee (lait)', allergenes: 'Lait',
  famille: familleMP, familleId: ENUM_FAM_MP,
  unite: uniteKG, uniteId: ENUM_UNIT_KG, uniteCode: 'KG',
  withLot: true, withStock: true, withTraca: true,
  net_weight_uc: 10, pcb: 1, articleId: ART_BEURRE,
  infosPrixAchat: [{ quantiteMin: 0, prix: 5.40, isPrixAchat: true, articleFourniId: null }],
  infosPrixVente: [{ quantiteMin: 0, prix: 7.80, isPrixAchat: false, articleFourniId: null }],
});

// Sucre : Traça seulement (pas de PDC)
const articleInfosSucre = makeArticleInfos({
  id: AI_SUCRE, denomination: 'Sucre cristal', code: 'MP-SUC-003',
  ean13: '3012345678925', bio: false,
  ingredients: 'Sucre de betterave', allergenes: '',
  famille: familleMP, familleId: ENUM_FAM_MP,
  unite: uniteKG, uniteId: ENUM_UNIT_KG, uniteCode: 'KG',
  withLot: true, withStock: true, withTraca: true,
  net_weight_uc: 25, pcb: 1, articleId: ART_SUCRE,
  infosPrixAchat: [{ quantiteMin: 0, prix: 0.72, isPrixAchat: true, articleFourniId: null }],
  infosPrixVente: [{ quantiteMin: 0, prix: 1.05, isPrixAchat: false, articleFourniId: null }],
});

// Oeufs : Traça seulement (pas de PDC)
const articleInfosOeufs = makeArticleInfos({
  id: AI_OEUFS, denomination: 'Oeufs frais calibre M', code: 'MP-OEU-004',
  ean13: '3012345678932', bio: false,
  ingredients: 'Oeuf de poule', allergenes: 'Oeufs',
  famille: familleMP, familleId: ENUM_FAM_MP,
  unite: uniteU, uniteId: ENUM_UNIT_U, uniteCode: 'UNITE',
  withLot: true, withStock: true, withTraca: true,
  net_weight_uc: 0.06, pcb: 30, articleId: ART_OEUFS,
  infosPrixAchat: [{ quantiteMin: 0, prix: 0.15, isPrixAchat: true, articleFourniId: null }],
  infosPrixVente: [{ quantiteMin: 0, prix: 0.22, isPrixAchat: false, articleFourniId: null }],
});

// Lait : Déclaration des consommations (consoTraca, pas de PDC)
const articleInfosLait = makeArticleInfos({
  id: AI_LAIT, denomination: 'Lait entier pasteurise', code: 'MP-LAI-005',
  ean13: '3012345678949', bio: false,
  ingredients: 'Lait entier de vache', allergenes: 'Lait',
  famille: familleMP, familleId: ENUM_FAM_MP,
  unite: uniteL, uniteId: ENUM_UNIT_L, uniteCode: 'L',
  withLot: true, withStock: true, withTraca: true, withConsoTraca: true,
  net_weight_uc: 1, pcb: 6, articleId: ART_LAIT,
  infosPrixAchat: [{ quantiteMin: 0, prix: 0.95, isPrixAchat: true, articleFourniId: null }],
  infosPrixVente: [{ quantiteMin: 0, prix: 1.35, isPrixAchat: false, articleFourniId: null }],
});

const articleInfosBiscuits = makeArticleInfos({
  id: AI_BISCUITS, denomination: 'Biscuits sables du Moulin', code: 'PF-BIS-001',
  ean13: '3700799000006', bio: false,
  ingredients: 'Farine de ble (gluten), beurre (lait), sucre, oeufs, lait',
  allergenes: 'Gluten, Lait, Oeufs',
  famille: famillePF, familleId: ENUM_FAM_PF,
  sousFamille: { id: ENUM_SOUS_FAM_BISC, denomination: 'Biscuits', type: 'SOUS_FAMILLE_ARTICLE', uniteRef: null, coefficient: 1, parentId: ENUM_FAM_PF, isActive: true },
  sousFamilleId: ENUM_SOUS_FAM_BISC,
  unite: uniteKG, uniteId: ENUM_UNIT_KG, uniteCode: 'KG',
  withLot: true, withStock: true, withSpecif: true,
  net_weight_uc: 0.250, pcb: 12, articleId: ART_BISCUITS,
  infosPrixAchat: [],
  infosPrixVente: [{ quantiteMin: 0, prix: 12.50, isPrixAchat: false, articleFourniId: null }],
});

const articleInfosList = [
  articleInfosFarine, articleInfosBeurre, articleInfosSucre,
  articleInfosOeufs, articleInfosLait, articleInfosBiscuits,
];

// ---------------------------------------------------------------------------
// Articles
// ---------------------------------------------------------------------------

function makeArticle(id: string, infos: any, semId?: string): any {
  return {
    id, isActive: true, version: 1,
    inventaireId: null, articleInfos: infos,
    stockReappro: 0, stockSecurite: 0, quantiteOfDefault: 0, qu: 0,
    specificationEtapeMasterId: semId ?? null, modelSpecificationEtapeMasterId: null,
    planifDisplayMode: 'flux',
    gross_weight_uc: 0, net_weight_uc: infos.net_weight_uc,
    longueur_uc: 0, hauteur_uc: 0, largeur_uc: 0,
    gross_weight_colis: 0, net_weight_colis: 0,
    longueur_colis: 0, hauteur_colis: 0, largeur_colis: 0,
    pcb: infos.pcb, weight_suremballage: 0,
    articleFournis: [], denominationCommerciale: '', tva: 5.5,
    nbChangementLots: 0, sumConsoChangementLots: 0,
    unite: infos.uniteCode, qttUnitaire: 0, qttTheoriqueTotale: 0,
  };
}

const articles = [
  makeArticle(ART_FARINE, articleInfosFarine, SEM_IDS[0]),
  makeArticle(ART_BEURRE, articleInfosBeurre, SEM_IDS[1]),
  makeArticle(ART_SUCRE,  articleInfosSucre,  SEM_IDS[2]),
  makeArticle(ART_OEUFS,  articleInfosOeufs,  SEM_IDS[3]),
  makeArticle(ART_LAIT,   articleInfosLait,   SEM_IDS[4]),
  makeArticle(ART_BISCUITS, articleInfosBiscuits, SEM_IDS[5]),
];

// ---------------------------------------------------------------------------
// Adresses
// ---------------------------------------------------------------------------

const adresses = [
  {
    id: ADR_MOULIN, denomination: 'Moulin de Provence - Siege',
    titre: null, titreCode: null, nom: 'Reynaud', prenom: 'Pierre',
    telephone: '04 90 12 34 56', mail: 'contact@moulindeprovence.fr',
    infoOuverture: 'Lun-Ven 7h-17h', commentaire: '',
    numero: '12', numeroComplement: null, voie: 'Route des Alpilles',
    complement: 'Zone Artisanale', complement2: '',
    cp: '13520', commune: 'Maussane-les-Alpilles', pays: 'France',
    livraisonParDefaut: true, tiersId: null,
    isPiedDeChaine: false, isZoneReception: false, isZoneExpedition: false,
    isZoneNonConforme: false, isZoneQuarantaine: false, isZoneTransfert: false,
    adresseRefId: null, commandeId: null, ofId: null, isPerso: false, isActive: true,
  },
  {
    id: ADR_BOULANG, denomination: 'Boulangerie Martin - Boutique',
    titre: null, titreCode: null, nom: 'Martin', prenom: 'Sophie',
    telephone: '04 91 23 45 67', mail: 'commandes@boulangerie-martin.fr',
    infoOuverture: 'Mar-Sam 6h-19h', commentaire: '',
    numero: '8', numeroComplement: null, voie: 'Rue de la Republique',
    complement: '', complement2: '',
    cp: '13001', commune: 'Marseille', pays: 'France',
    livraisonParDefaut: true, tiersId: TIERS_BOULANG,
    isPiedDeChaine: false, isZoneReception: false, isZoneExpedition: false,
    isZoneNonConforme: false, isZoneQuarantaine: false, isZoneTransfert: false,
    adresseRefId: null, commandeId: null, ofId: null, isPerso: false, isActive: true,
  },
  {
    id: ADR_SUPER, denomination: 'Supermarche FraisMalin - Entrepot',
    titre: null, titreCode: null, nom: 'Dubois', prenom: 'Laurent',
    telephone: '04 42 56 78 90', mail: 'logistique@fraismalin.fr',
    infoOuverture: 'Lun-Sam 5h-21h', commentaire: 'Quai de dechargement n3',
    numero: '150', numeroComplement: null, voie: 'Avenue du Commerce',
    complement: 'ZAC Les Milles', complement2: '',
    cp: '13290', commune: 'Aix-en-Provence', pays: 'France',
    livraisonParDefaut: true, tiersId: TIERS_SUPER,
    isPiedDeChaine: false, isZoneReception: false, isZoneExpedition: false,
    isZoneNonConforme: false, isZoneQuarantaine: false, isZoneTransfert: false,
    adresseRefId: null, commandeId: null, ofId: null, isPerso: false, isActive: true,
  },
];

// ---------------------------------------------------------------------------
// Tiers
// ---------------------------------------------------------------------------

const tiers = [
  {
    id: TIERS_MOULIN, denomination: 'Moulin de Provence',
    docId: null, telephone: '04 90 12 34 56', mail: 'contact@moulindeprovence.fr',
    mailCommande: 'commandes@moulindeprovence.fr', mailCommandeClient: '',
    mailFacturation: 'compta@moulindeprovence.fr', mailLivraison: 'logistique@moulindeprovence.fr',
    siret: '412 345 678 00012', naf: '1061A', tva_intracom: 'FR12412345678',
    reduction: 0, francoQuantite: 500, francoPrix: 0, type: 'FOURNISSEUR',
    adresses: [adresses[0]], isTransporteur: false,
    contact: [], primaryContact: null, articlesFournis: [],
    typeReglement: 'VIREMENT', delaiReglement: 'FDM', delaiReglementJour: 30,
    adresseLivraison: adresses[0], adresseLivraisonId: ADR_MOULIN,
    adresseFacturation: adresses[0], adresseFacturationId: ADR_MOULIN,
    ribPaiement: null, tierGroups: [], isActive: true,
  },
  {
    id: TIERS_BOULANG, denomination: 'Boulangerie Martin',
    docId: null, telephone: '04 91 23 45 67', mail: 'contact@boulangerie-martin.fr',
    mailCommande: '', mailCommandeClient: 'commandes@boulangerie-martin.fr',
    mailFacturation: 'compta@boulangerie-martin.fr', mailLivraison: '',
    siret: '523 456 789 00034', naf: '1071C', tva_intracom: 'FR23523456789',
    reduction: 0, francoQuantite: 0, francoPrix: 0, type: 'CLIENT',
    adresses: [adresses[1]], isTransporteur: false,
    contact: [], primaryContact: null, articlesFournis: [],
    typeReglement: 'VIREMENT', delaiReglement: 'FDM', delaiReglementJour: 30,
    adresseLivraison: adresses[1], adresseLivraisonId: ADR_BOULANG,
    adresseFacturation: adresses[1], adresseFacturationId: ADR_BOULANG,
    ribPaiement: null, tierGroups: [], isActive: true,
  },
  {
    id: TIERS_SUPER, denomination: 'Supermarche FraisMalin',
    docId: null, telephone: '04 42 56 78 90', mail: 'contact@fraismalin.fr',
    mailCommande: '', mailCommandeClient: 'achat@fraismalin.fr',
    mailFacturation: 'factures@fraismalin.fr', mailLivraison: 'logistique@fraismalin.fr',
    siret: '634 567 890 00056', naf: '4711F', tva_intracom: 'FR34634567890',
    reduction: 2, francoQuantite: 0, francoPrix: 300, type: 'CLIENT',
    adresses: [adresses[2]], isTransporteur: false,
    contact: [], primaryContact: null, articlesFournis: [],
    typeReglement: 'PRELEVEMENT', delaiReglement: 'FDM', delaiReglementJour: 45,
    adresseLivraison: adresses[2], adresseLivraisonId: ADR_SUPER,
    adresseFacturation: adresses[2], adresseFacturationId: ADR_SUPER,
    ribPaiement: null, tierGroups: [], isActive: true,
  },
];

// ---------------------------------------------------------------------------
// Lots
// ---------------------------------------------------------------------------

const _now = new Date();
const _inSixMonths       = new Date(_now.getFullYear(), _now.getMonth() + 6, _now.getDate()).toISOString();
const _inTwelveMonths    = new Date(_now.getFullYear(), _now.getMonth() + 12, _now.getDate()).toISOString();
const _inTwentyFourMonths = new Date(_now.getFullYear(), _now.getMonth() + 24, _now.getDate()).toISOString();
const _inTwentyEightDays = new Date(_now.getFullYear(), _now.getMonth(), _now.getDate() + 28).toISOString();
const _inSevenDays       = new Date(_now.getFullYear(), _now.getMonth(), _now.getDate() + 7).toISOString();

function makeLot(
  id: string, articleId: string, article: any, infos: any,
  numero: string, dluo: string, quantite: number, cout: number,
): any {
  return {
    id, articleId, article, numero, dluo, dluoString: dluo,
    enQuarantaine: false, mouvements: [], receptionsCommandes: [],
    documentListeId: null, inventaireId: null, groupId: null,
    codeArticle: infos.code, quantite: String(quantite),
    dateMouvement: _now.toISOString(), dateCreation: _now,
    coutMaterielUnitaire: cout, coutPersonnelUnitaire: 0,
    lotUnique: false, quantiteDisplay: quantite,
    ofId: null, ofNumero: null, articleInfos: infos, stocks: [], isActive: true,
  };
}

const lots = [
  makeLot(LOT_FARINE, ART_FARINE, articles[0], articleInfosFarine, 'FAR-2026-001', _inTwelveMonths,    500, 0.85),
  makeLot(LOT_BEURRE, ART_BEURRE, articles[1], articleInfosBeurre, 'BEU-2026-001', _inSixMonths,       100, 5.40),
  makeLot(LOT_SUCRE,  ART_SUCRE,  articles[2], articleInfosSucre,  'SUC-2026-001', _inTwentyFourMonths, 300, 0.72),
  makeLot(LOT_OEUFS,  ART_OEUFS,  articles[3], articleInfosOeufs,  'OEU-2026-001', _inTwentyEightDays,  360, 0.15),
  makeLot(LOT_LAIT,   ART_LAIT,   articles[4], articleInfosLait,   'LAI-2026-001', _inSevenDays,        200, 0.95),
  makeLot(LOT_BISCUITS, ART_BISCUITS, articles[5], articleInfosBiscuits, 'BIS-2026-001', _inSixMonths, 0, 0),
];

// ---------------------------------------------------------------------------
// Commandes fournisseur
// ---------------------------------------------------------------------------

const _threeDaysAgo = new Date(_now.getFullYear(), _now.getMonth(), _now.getDate() - 3).toISOString();
const _fiveDaysAgo  = new Date(_now.getFullYear(), _now.getMonth(), _now.getDate() - 5).toISOString();
const _inTwoDays    = new Date(_now.getFullYear(), _now.getMonth(), _now.getDate() + 2).toISOString();
const _inFourDays   = new Date(_now.getFullYear(), _now.getMonth(), _now.getDate() + 4).toISOString();

function makeCfLigne(
  id: string, articleId: string, quantite: number, prix: number,
  uniteGestion: string, refFournisseur: string, commandeId: string, index: number,
): any {
  return {
    id, articleFourniId: null, articleId, quantite, quantiteAchat: quantite,
    prix, prixAvantRemise: prix, prixManuel: false, tva: 5.5,
    uniteGestion, coefficientUniteGestion: 1, refFournisseur,
    commandeId, receptions: [], commande: null, index, remiseLignes: [], isActive: true,
  };
}

const commandesFournisseur = [
  {
    id: CF_001, etat: 'EN_ATTENTE_LIVRAISON', numero: 'CF2600001',
    adresseLivraison: adresses[0], adresseLivraisonId: ADR_MOULIN,
    commentaire: 'Livraison matieres premieres semaine 20',
    provisoire: false, tiers: tiers[0], tiersId: TIERS_MOULIN,
    lignes: [
      makeCfLigne(CFL_001, ART_FARINE, 500, 0.85, 'KG', 'FAR-T65-25', CF_001, 0),
      makeCfLigne(CFL_002, ART_BEURRE, 100, 5.40, 'KG', 'BEU-82-10',  CF_001, 1),
    ],
    dateLivraisonPrevue: _inTwoDays, dateCreation: _fiveDaysAgo, dateValidation: _threeDaysAgo,
    dateFermeture: null, dateSoldee: null, dateAnnulation: null, dateManuelle: false,
    annulateur: null, annulateurId: null, createur: null, createurId: null,
    soldeur: null, soldeurId: null, validator: null, validatorId: null,
    documentListeId: null, isActive: true,
  },
  {
    id: CF_002, etat: 'EN_REDACTION', numero: 'CF2600002',
    adresseLivraison: adresses[0], adresseLivraisonId: ADR_MOULIN,
    commentaire: '', provisoire: false, tiers: tiers[0], tiersId: TIERS_MOULIN,
    lignes: [
      makeCfLigne(CFL_003, ART_SUCRE, 300, 0.72, 'KG', 'SUC-CRIST-25', CF_002, 0),
    ],
    dateLivraisonPrevue: _inFourDays, dateCreation: _now.toISOString(), dateValidation: null,
    dateFermeture: null, dateSoldee: null, dateAnnulation: null, dateManuelle: false,
    annulateur: null, annulateurId: null, createur: null, createurId: null,
    soldeur: null, soldeurId: null, validator: null, validatorId: null,
    documentListeId: null, isActive: true,
  },
  {
    id: CF_003, etat: 'EN_REDACTION', numero: 'CF2600003',
    adresseLivraison: adresses[0], adresseLivraisonId: ADR_MOULIN,
    commentaire: '', provisoire: false, tiers: tiers[0], tiersId: TIERS_MOULIN,
    lignes: [],
    dateLivraisonPrevue: _inFourDays, dateCreation: _now.toISOString(), dateValidation: null,
    dateFermeture: null, dateSoldee: null, dateAnnulation: null, dateManuelle: false,
    annulateur: null, annulateurId: null, createur: null, createurId: null,
    soldeur: null, soldeurId: null, validator: null, validatorId: null,
    documentListeId: null, isActive: true,
    // This commande is only visible after the tuto creates it (availableAt = section 3 progress)
    availableAt: 15.25,
  },
];

// ---------------------------------------------------------------------------
// Commandes client
// ---------------------------------------------------------------------------

function makeCcLigne(
  id: string, articleId: string, quantite: number, prix: number,
  uniteGestion: string, code: string, label: string,
  commandeClientId: string, index: number, refClient: string,
): any {
  return {
    id, articleFourniId: null, articleId, expeditions: [],
    quantite, quantiteArticle: quantite, quantiteLivree: 0, quantiteFacturee: 0,
    uniteGestion, prix, prixAvantRemise: prix, prixManuel: false, tva: 5.5,
    uuid: `ccl-uuid-${id.slice(-3)}`, codeArticle: code,
    commandeClient: null, commandeClientId, coefficientUniteGestion: 1,
    refClient, label, ofId: null, of: null, index,
    remiseLignes: [], discountType: null, discountValue: 0, isActive: true,
  };
}

const commandesClient = [
  {
    id: CC_001, numero: 'CC2600001', numeroCommandeClient: 'BM-2026-042',
    etat: 'EN_COURS_DE_TRAITEMENT', etatPaiement: null,
    etatExpedition: null, etatFacturation: null, facturesProvisoires: false,
    adresseLivraison: adresses[1], adresseLivraisonId: ADR_BOULANG,
    adresseFacturation: adresses[1], adresseFacturationId: ADR_BOULANG,
    entrepotDepart: null, entrepotDepartId: null, entrepots: [],
    tiers: tiers[1], tiersId: TIERS_BOULANG,
    lignes: [
      makeCcLigne(CCL_001, ART_BISCUITS, 100, 12.50, 'KG', 'PF-BIS-001', 'Biscuits sables du Moulin', CC_001, 0, ''),
    ],
    dateLivraisonPrevue: _inTwoDays, dateAnnulation: null,
    createur: null, createurId: null,
    dateValidation: _threeDaysAgo, validator: null, validatorId: null,
    dateCreation: _fiveDaysAgo,
    datePreparation: null, preparator: null, preparatorId: null,
    datePrete: null, pretator: null, pretatorId: null,
    dateExpedition: null, expeditor: null, expeditorId: null,
    dateTermine: null, terminator: null, terminatorId: null,
    documentListeId: null,
    remisePied: null, remisePiedType: null, remisePiedLabel: null,
    statusHistory: null, isActive: true,
  },
  {
    id: CC_002, numero: 'CC2600002', numeroCommandeClient: 'FM-PO-8834',
    etat: 'EN_ATTENTE_CONFIRMATION', etatPaiement: null,
    etatExpedition: null, etatFacturation: null, facturesProvisoires: false,
    adresseLivraison: adresses[2], adresseLivraisonId: ADR_SUPER,
    adresseFacturation: adresses[2], adresseFacturationId: ADR_SUPER,
    entrepotDepart: null, entrepotDepartId: null, entrepots: [],
    tiers: tiers[2], tiersId: TIERS_SUPER,
    lignes: [
      makeCcLigne(CCL_003, ART_BISCUITS, 250, 12.50, 'KG', 'PF-BIS-001', 'Biscuits sables du Moulin', CC_002, 0, 'REF-FM-BIS'),
    ],
    dateLivraisonPrevue: _inFourDays, dateAnnulation: null,
    createur: null, createurId: null,
    dateValidation: null, validator: null, validatorId: null,
    dateCreation: _now.toISOString(),
    datePreparation: null, preparator: null, preparatorId: null,
    datePrete: null, pretator: null, pretatorId: null,
    dateExpedition: null, expeditor: null, expeditorId: null,
    dateTermine: null, terminator: null, terminatorId: null,
    documentListeId: null,
    remisePied: null, remisePiedType: null, remisePiedLabel: null,
    statusHistory: null, isActive: true,
  },
];

// ---------------------------------------------------------------------------
// Exported demo data (also usable directly for targeted lookups)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// SpecificationEtapeMaster (wraps articles for the /specification_etape/master endpoint)
// ---------------------------------------------------------------------------

function makeSEM(semId: string, article: any, infos: any, seOverride?: string, etat = 'EN_REDACTION'): any {
  const seId = seOverride ?? (semId + '-se');
  return {
    id: semId,
    isActive: true,
    version: 1,
    denomination: infos.denomination,
    description: null,
    articleId: article.id,
    parentId: null,
    parent: null,
    childs: [],
    documentListeId: null,
    uniteGestion: infos.uniteCode,
    versions: [{
      id: seId,
      version_nb: 1,
      etat,
      infosArticleId: infos.id,
      infosArticle: infos,
      masterId: semId,
      nomenclatureId: null,
      nomenclature: null,
      process: [],
      podControlesProduction: null,
      podControlesReceptionsId: null,
      podControlesReceptions: null,
      podControlesDechargementId: null,
      podControlesDechargement: null,
      podControlesStartOpeId: null,
      podControlesStartOpe: null,
      podControlesEndOpeId: null,
      podControlesEndOpe: null,
      dateRedaction: new Date().toISOString(),
      dateValidation: new Date().toISOString(),
    }],
  };
}

const specificationMasters: any[] = [
  makeSEM(SEM_IDS[0], articles[0], articleInfosFarine),
  makeSEM(SEM_IDS[1], articles[1], articleInfosBeurre),
  makeSEM(SEM_IDS[2], articles[2], articleInfosSucre),
  makeSEM(SEM_IDS[3], articles[3], articleInfosOeufs),
  makeSEM(SEM_IDS[4], articles[4], articleInfosLait, undefined, 'EN_REDACTION'),
  makeSEM(SEM_IDS[5], articles[5], articleInfosBiscuits, SE_BISCUITS, 'EN_REDACTION'),
];

// Pre-populate podIds on Lait (reception) and Biscuits (start ope) so tutorial doesn't need createPod
specificationMasters[4].versions[0].podControlesReceptionsId = POD_RECEP_LAIT;
specificationMasters[4].versions[0].podControlesReceptions = { id: POD_RECEP_LAIT, controles: [] };
specificationMasters[5].versions[0].podControlesStartOpeId = POD_STARTOPE_BISC;
specificationMasters[5].versions[0].podControlesStartOpe = { id: POD_STARTOPE_BISC, controles: [] };

// enrichBiscuitsSEM() is called after nomenclature/process/controles are defined (see below)

// ---------------------------------------------------------------------------
// Nomenclature (recipe for Biscuits sables du Moulin)
// ---------------------------------------------------------------------------

function makeNomenclatureLigne(
  id: string, articleId: string, articleCode: string, quantite: number,
  perteNormale: number, perteMax: number, index: number, infos: any,
): any {
  return {
    id, articleId, articleCode, quantite,
    perteNormale, perteMax,
    tiers: null, tiersId: null,
    specificationEtapeRefId: SE_BISCUITS,
    optional: false, articleDynamic: false, paramDynamic: false,
    description: '', parsedDescription: '', index,
    bricks: [], dontDo: false, lotHeritier: index === 0,
    isActive: true, version: 1,
    articleInfos: infos,
  };
}

// Quantités réalistes pour 250 kg de biscuits sablés (quantityProdDefault = 250)
// consoTheoriqueUnitaire = qty / 250, consoTheorique = consoTheoriqueUnitaire * 250
const nomenclatureLignes = [
  makeNomenclatureLigne(NOM_L_FARINE, ART_FARINE, 'MP-FAR-001', 100,   2, 5, 0, articleInfosFarine),  // 100 kg farine (40%)
  makeNomenclatureLigne(NOM_L_BEURRE, ART_BEURRE, 'MP-BEU-002',  62.5, 1, 3, 1, articleInfosBeurre),  //  62.5 kg beurre (25%)
  makeNomenclatureLigne(NOM_L_SUCRE,  ART_SUCRE,  'MP-SUC-003',  50,   1, 2, 2, articleInfosSucre),   //  50 kg sucre (20%)
  makeNomenclatureLigne(NOM_L_OEUFS,  ART_OEUFS,  'MP-OEU-004',  25,   2, 5, 3, articleInfosOeufs),   //  25 kg oeufs (10%)
  makeNomenclatureLigne(NOM_L_LAIT,   ART_LAIT,   'MP-LAI-005',  12.5, 1, 3, 4, articleInfosLait),    //  12.5 L lait (5%)
];

const nomenclatures = [
  {
    id: NOM_BISCUITS,
    isActive: true,
    version: 1,
    nomenclatureLignes,
    articleCode: 'PF-BIS-001',
    article: articles[5],
    specificationEtapeId: SE_BISCUITS,
    specificationEtape: null,
  },
];

// ---------------------------------------------------------------------------
// Process Etapes (fabrication steps for Biscuits)
// ---------------------------------------------------------------------------

function makeProcessEtape(
  id: string, index: number, label: string, description: string,
  timer: string, articleId: string,
  podId?: string,
): any {
  return {
    id, index, uuid: id, label, description,
    articleId,
    podId: podId ?? null, pod: null,
    docId: null, documentListeId: null,
    timer,
    parent: null, lignes: [],
    optional: false,
    specificationEtapeId: SE_BISCUITS,
    specificationEtapeRefId: SE_BISCUITS,
    done: false,
    promptAI: null,
    dontDo: false,
    controles: [],
    isActive: true, version: 1,
  };
}

const processEtapes = [
  makeProcessEtape(PE_MELANGE,   1, 'Melange sec',           'Melanger la farine et le sucre dans le petrin jusqu\'a obtenir un melange homogene.',           '5',  ART_BISCUITS, POD_MELANGE),
  makeProcessEtape(PE_INCORP_B,  2, 'Incorporation beurre',  'Ajouter le beurre froid coupe en des. Sabler la pate jusqu\'a obtenir une texture sableuse.',   '10', ART_BISCUITS),
  makeProcessEtape(PE_AJOUT_OL,  3, 'Ajout oeufs et lait',   'Incorporer les oeufs battus et le lait. Melanger sans trop travailler la pate.',                '5',  ART_BISCUITS),
  makeProcessEtape(PE_REPOS,     4, 'Repos de la pate',       'Filmer la pate et la laisser reposer au refrigerateur.',                                        '30', ART_BISCUITS),
  makeProcessEtape(PE_FACONNAGE, 5, 'Faconnage',              'Etaler la pate a 5mm d\'epaisseur et decouper les biscuits a l\'emporte-piece.',                 '15', ART_BISCUITS),
  makeProcessEtape(PE_CUISSON,   6, 'Cuisson',                'Enfourner a 175 C. Surveiller la coloration : les biscuits doivent etre dores mais pas bruns.',  '20', ART_BISCUITS, POD_CUISSON),
  makeProcessEtape(PE_REFROID,   7, 'Refroidissement',        'Laisser refroidir les biscuits sur grille a temperature ambiante.',                              '30', ART_BISCUITS),
  makeProcessEtape(PE_CONDITION, 8, 'Conditionnement',        'Peser et conditionner les biscuits en sachets de 250g. Etiqueter et dater.',                     '10', ART_BISCUITS, POD_CONDITION),
];

// ---------------------------------------------------------------------------
// Controles qualite
// ---------------------------------------------------------------------------

const controlePod: any = {
  id: CTRL_POD,
  isActive: true,
  version: 1,
  controles: [],  // populated below
};

function makeControle(overrides: Record<string, any>): any {
  return {
    id: overrides['id'],
    userId: null,
    index: overrides['index'] ?? 0,
    date: new Date(),
    type: overrides['type'],
    etat: 'EN_COURS',
    description: overrides['description'] ?? '',
    podId: CTRL_POD,
    docId: null,
    documentListeId: null,
    consigneNC: overrides['consigneNC'] ?? '',
    identifiantObligatoire: false,
    denomination: overrides['denomination'],
    events: '',
    controleur: null, controleurId: null,
    posteEvenement: null, posteEvenementId: null,
    tailleEchantillon: overrides['tailleEchantillon'] ?? 1,
    tailleEchantillonString: String(overrides['tailleEchantillon'] ?? 1),
    marquage: null,
    choix: overrides['choix'] ?? '',
    correctAnswer: overrides['correctAnswer'] ?? '',
    isPhoto: false, isValeur: false,
    unite: overrides['unite'] ?? '',
    avecCourbe: false,
    limiteHaute: overrides['limiteHaute'] ?? 0,
    limiteBasse: overrides['limiteBasse'] ?? 0,
    nominal: overrides['nominal'] ?? 0,
    limiteHauteString: String(overrides['limiteHaute'] ?? 0),
    limiteBasseString: String(overrides['limiteBasse'] ?? 0),
    nominalString: String(overrides['nominal'] ?? 0),
    typeNominal: overrides['typeNominal'] ?? null,
    moyenne: 0, valeurDynamique: false,
    isEcart: false, valeur1: '', valeur2: '',
    isJustupTodo: false, ccp: overrides['ccp'] ?? false, prpo: false,
    elementType: overrides['elementType'] ?? 'PRODUCTION',
    elementId: null, elementControlledId: null,
    parentControl: null, checklistId: null,
    model: true,
    enregistrements: [],
    uuid: overrides['id'],
    isValid: false,
    ecartMinutesString: '', toleranceMinutesString: '',
    processEtape: null, processEtapeId: overrides['processEtapeId'] ?? null,
    processEtapeRef: null, processEtapeRefId: null,
    specificationEtapeId: SE_BISCUITS, specificationEtapeRefId: SE_BISCUITS,
    articleId: ART_BISCUITS, articleVariableUUID: null,
    lignes: [], event: '',
    echantillonParQuantite: 0, controleParQuantite: false,
    articleInfosRefId: null,
    familles: [], categories: [], tiers: [], clients: [],
    expeditionUnitTypes: [], entrepots: [],
    valid: false,
    isActive: true, version: 1,
  };
}

// Contrôle 1 : CNC one-shot sur Mélange sec (pré-existant, visible dès section 3)
const ctrlAspectMelange: any = makeControle({
  id: CTRL_ASPECT, index: 1,
  denomination: 'Aspect du melange',
  description: 'Verifier que le melange farine-sucre est homogene, sans grumeaux visibles.',
  type: 'CNC',
  podId: POD_MELANGE,
  processEtapeId: PE_MELANGE,
  elementType: 'PROCESS',
  consigneNC: 'Prolonger le melange 2 minutes supplementaires et reverifier.',
});
ctrlAspectMelange.availableAt = 7.12; // visible when section 3 starts

// Contrôle 2 : Quantitatif récurrent sur Cuisson (toutes les 15 min, CCP)
const ctrlTempCuisson: any = makeControle({
  id: CTRL_TEMP, index: 1,
  denomination: 'Temperature du four',
  description: 'Mesurer la temperature du four avec le thermometre etalone.',
  type: 'QUANTITATIF',
  unite: '°C',
  limiteBasse: 170, limiteHaute: 180, nominal: 175,
  typeNominal: 'BETWEEN',
  ccp: true,
  podId: POD_CUISSON,
  processEtapeId: PE_CUISSON,
  elementType: 'PROCESS',
  consigneNC: 'Ajuster la temperature du four et attendre la stabilisation avant d\'enfourner.',
});
ctrlTempCuisson.ecartMinutesString = '15';
ctrlTempCuisson.toleranceMinutesString = '5';
ctrlTempCuisson.availableAt = 7.12;
ctrlTempCuisson.toleranceMinutesString = '5';

// Contrôle 3 : CNC sur événement sur Conditionnement
const ctrlEtiquette: any = makeControle({
  id: CTRL_ETIQ, index: 1,
  denomination: 'Changement rouleau etiquettes',
  description: 'Verifier la conformite de l\'etiquetage apres chaque changement de rouleau : lisibilite, DLC, code-barres.',
  type: 'CNC',
  podId: POD_CONDITION,
  processEtapeId: PE_CONDITION,
  elementType: 'PROCESS',
  consigneNC: 'Retirer les sachets etiquetes depuis le dernier controle et re-etiqueter.',
});
ctrlEtiquette.events = 'Changement rouleau etiquettes';
ctrlEtiquette.availableAt = 7.12;

// Contrôle 4 : Poids (gardé pour la production)
const ctrlPoids: any = makeControle({
  id: CTRL_POIDS, index: 2,
  denomination: 'Poids sachet',
  description: 'Peser un sachet de biscuits pour verifier le poids net.',
  type: 'QUANTITATIF',
  unite: 'g',
  limiteBasse: 245, limiteHaute: 255, nominal: 250,
  typeNominal: 'BETWEEN',
  tailleEchantillon: 3,
  podId: POD_CONDITION,
  processEtapeId: PE_CONDITION,
  elementType: 'PROCESS',
  consigneNC: 'Verifier le reglage de la peseuse. Ecarter les sachets hors tolerance.',
});
ctrlPoids.availableAt = 7.12;

// Contrôle 5 : CNC one-shot sur Mélange sec (créé par l'utilisateur pendant le tuto)
const ctrlHomogPate: any = makeControle({
  id: CTRL_HOMOG, index: 2,
  denomination: 'Homogeneite pate',
  description: 'Verifier texture uniforme, sans grumeaux.',
  type: 'CNC',
  podId: POD_MELANGE,
  processEtapeId: PE_MELANGE,
  elementType: 'PROCESS',
  consigneNC: 'Prolonger le petrissage et reverifier.',
});
ctrlHomogPate.availableAt = 7.27; // visible after proc-save-controle step

const controles = [ctrlAspectMelange, ctrlTempCuisson, ctrlEtiquette, ctrlPoids, ctrlHomogPate];

// Build pods with their controles
const podMelange: any = { id: POD_MELANGE, isActive: true, version: 1, controles: [ctrlAspectMelange, ctrlHomogPate] };
const podCuisson: any = { id: POD_CUISSON, isActive: true, version: 1, controles: [ctrlTempCuisson] };
const podCondition: any = { id: POD_CONDITION, isActive: true, version: 1, controles: [ctrlEtiquette, ctrlPoids] };
const controlePods = [podMelange, podCuisson, podCondition];

controlePod.controles = controles;

// Enrich processEtapes with their controles from pods
processEtapes.forEach((pe: any) => {
  const pod = controlePods.find((p: any) => p.id === pe.podId);
  if (pod) {
    pe.controles = pod.controles;
    pe.pod = pod;
  }
});

// Enrich Biscuits SEM with nomenclature + process + controles now that they're defined
(() => {
  const sem = specificationMasters[5];
  const se = sem.versions[0];
  se.nomenclatureId = NOM_BISCUITS;
  se.nomenclature = nomenclatures[0];
  se.process = processEtapes;
  se.podControlesProduction = controlePod;
})();

// ---------------------------------------------------------------------------
// Ligne de production
// ---------------------------------------------------------------------------

const ligneParam: any = {
  id: LIGNE_PARAM,
  ligneId: LIGNE_BISC,
  familleTechniqueId: ENUM_SOUS_FAM_BISC,
  familleTechnique: { id: ENUM_SOUS_FAM_BISC, denomination: 'Biscuits', type: 'SOUS_FAMILLE_ARTICLE', parentId: ENUM_FAM_PF, isActive: true },
  cadence: 50,
  nbPersonnes: 3,
  tempsCalage: 15,
  tempsNettoyage: 20,
  dureePoste: 480,
  isActive: true,
  version: 1,
};

const lignes: any[] = [
  {
    id: LIGNE_BISC,
    denomination: 'Ligne Biscuits',
    adresse: adresses[0],
    adresseId: ADR_MOULIN,
    docId: null,
    groupId: null,
    nbOF: 1,
    params: [ligneParam],
    firstParam: [],
    horaires: [{
      id: 'hor-ligne-bisc',
      ligneId: LIGNE_BISC,
      lastChangedDateTime: new Date('2026-01-01'),
      lundi: ['09:00-17:00'],
      mardi: ['09:00-17:00'],
      mercredi: ['09:00-17:00'],
      jeudi: ['09:00-17:00'],
      vendredi: ['09:00-17:00'],
      samedi: [],
      dimanche: [],
    }],
    lists: [],
    entityLinks: [],
    user: null,
    isActive: true, version: 1,
  },
];

// ---------------------------------------------------------------------------
// Ordre de Fabrication
// ---------------------------------------------------------------------------

const _dayOffset = (days: number) => new Date(_now.getFullYear(), _now.getMonth(), _now.getDate() + days).toISOString();

/** Renvoie le Nième jour ouvré à partir d'aujourd'hui (0 = aujourd'hui si ouvré, sinon lundi). */
function _workDay(n: number, hours = 0, minutes = 0): string {
  const d = new Date(_now.getFullYear(), _now.getMonth(), _now.getDate());
  // Avance au premier jour ouvré si aujourd'hui est weekend
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  let count = 0;
  while (count < n) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0 && d.getDay() !== 6) count++;
  }
  d.setHours(hours, minutes, 0, 0);
  return d.toISOString();
}

function makeOf(id: string, numero: string, etat: string, quantite: number, quantiteProduite: number, startWorkDay: number, startH: number, endWorkDay: number | null, endH: number | null, started: boolean, available: number): any {
  const debut = _workDay(startWorkDay, startH);
  const fin = (endWorkDay != null && endH != null) ? _workDay(endWorkDay, endH) : null;
  return {
    id, numero, etat, pdcStatus: null,
    articleId: ART_BISCUITS, article: articles[5],
    articleInfos: articleInfosBiscuits, articleInfosId: AI_BISCUITS,
    quantite, quantiteProduite,
    integrations: [], started,
    documentListeId: null, statsDocumentListeId: null,
    valideProd: false, valideQualite: false,
    dateReinteTemp: null,
    dateDebut: debut,
    dateFin: fin,
    dateDebutPrevue: debut,
    commandeClientLignes: [],
    lots: [], postes: [], mouvements: [], bilans: [],
    bilanProductivite: null, qttReinteTemp: 0,
    scoreTotal: 0, scoreMatiere: 0, scorePersonnel: 0,
    scoreMatiereSurcout: 0, scorePersonnelSurcout: 0,
    coutMatiereTotalTheorique: 0, coutMatiereTotalReel: 0,
    coutPersonnelTotalTheorique: 0, coutPersonnelTotalReel: 0,
    coutNcMatiere: 0, coutNcPersonnel: 0, coutNcRecuperable: 0,
    paramLigne: ligneParam, paramLigneId: LIGNE_PARAM,
    ligne: lignes[0], ligneId: LIGNE_BISC,
    specificationId: null, bilanMatiereIntermediaire: false,
    withQuantiteOpe: false, ops: '', pathId: null,
    posteId: null, doc: null,
    isActive: true, version: 1,
    availableAt: available,
  };
}

// Horaires ligne : 09:00-17:00 (8h) lundi-vendredi. Cadence ~500u/8h.
// Les jours sont en JOURS OUVRÉS (0=aujourd'hui, 1=prochain jour ouvré, etc.)
//
// Progression availableAt (order=12, globalStepIndex/100) :
//   11.17 → OF1 apparaît à la création dans le tuto OF
//   12.00 → OF2 visible au début du tuto planif (pour le dialog +)
//   12.08 → OF3-10 cascade après section 2 (reloadAfter)
//
// Flow du tuto :
//   - Section 1 : juste OF1 visible sur le Gantt (planifié depuis le form OF)
//   - Section 2 : on présente le +, on explique le mécanisme, cascade
//   - Section 3 : vue 7j avec tous les OF, drag & drop, fonctionnalités
//                                                                         qty   qp  wd  hD  wd    hF   started avail
const ofs: any[] = [
  makeOf(OF_BISCUITS,                              'OF2600001', 'EN_ATTENTE_PROD',    250,  0,   0,  9,  0, 13,   false, 11.17),
  makeOf('of000002-aaaa-4bbb-cccc-dddddddddddd',  'OF2600002', 'EN_ATTENTE_PLANNIF', 200,  0,   0, 14,  null, null, false, 12.00),
  makeOf('of000003-aaaa-4bbb-cccc-dddddddddddd',  'OF2600003', 'EN_ATTENTE_PROD',    280,  0,   1,  9,  1, 13,   false, 12.08),
  makeOf('of000004-aaaa-4bbb-cccc-dddddddddddd',  'OF2600004', 'EN_ATTENTE_PROD',    230,  0,   2,  9,  2, 13,   false, 12.08),
  makeOf('of000005-aaaa-4bbb-cccc-dddddddddddd',  'OF2600005', 'EN_ATTENTE_PLANNIF', 260,  0,   3,  9,  null, null, false, 12.08),
  makeOf('of000006-aaaa-4bbb-cccc-dddddddddddd',  'OF2600006', 'EN_ATTENTE_PROD',    300,  0,   3,  9,  3, 14,   false, 12.08),
  makeOf('of000007-aaaa-4bbb-cccc-dddddddddddd',  'OF2600007', 'EN_ATTENTE_PLANNIF', 220,  0,   4,  9,  null, null, false, 12.08),
  makeOf('of000008-aaaa-4bbb-cccc-dddddddddddd',  'OF2600008', 'EN_ATTENTE_PROD',    270,  0,   4,  9,  4, 13,   false, 12.08),
  makeOf('of000009-aaaa-4bbb-cccc-dddddddddddd',  'OF2600009', 'EN_ATTENTE_PLANNIF', 240,  0,   5,  9,  null, null, false, 12.08),
  makeOf('of000010-aaaa-4bbb-cccc-dddddddddddd',  'OF2600010', 'EN_ATTENTE_PROD',    290,  0,   5,  9,  5, 14,   false, 12.08),
];

// ---------------------------------------------------------------------------
// Entreprise fictive
// ---------------------------------------------------------------------------

const activityInfos: any = {
  id: 'ai000001-aaaa-4bbb-cccc-dddddddddddd',
  denomination: 'Boulangerie du Moulin',
  fj: 'SARL',
  siret: '123 456 789 00012',
  capital: '50000',
  rcsVille: 'Bordeaux',
  rcsNumero: '123 456 789',
  codeAPE: '1071C',
  tvaIntracom: 'FR12345678901',
  email: 'contact@boulangerie-du-moulin.fr',
  emailFacture: 'compta@boulangerie-du-moulin.fr',
  telephone: '05 56 00 00 00',
  groupId: null,
  codeGS1: '370079900',
  textColor: false,
  color: '#2E7D32',
  devise: 'EUR',
  docId: null,
  infoCommandeFournisseur: '',
  infoFactureExpedition: '',
  infoAvoirClient: '',
  infoConfirmationCommandeClient: '',
  localIps: '',
  authorizedIps: '',
  horaires: [],
  offre: 'JUSTUP',
  validationSpecif: 'NONE',
  validationOF: 'SIMPLE',
  consigneNC: '',
  promptAIProcess: '',
  dateDebutExercice: '01/01',
  signatureOperateurObligatoire: false,
  isActive: true,
  version: 1,
};

// ---------------------------------------------------------------------------
// Helpers for traca module (progress >= 14) — pre-built poste and PEOs
// ---------------------------------------------------------------------------

const _PEO_UUIDS = [
  'b0b0b0b0-0001-4000-a000-000000000001',
  'b0b0b0b0-0002-4000-a000-000000000002',
  'b0b0b0b0-0003-4000-a000-000000000003',
  'b0b0b0b0-0004-4000-a000-000000000004',
  'b0b0b0b0-0005-4000-a000-000000000005',
  'b0b0b0b0-0006-4000-a000-000000000006',
  'b0b0b0b0-0007-4000-a000-000000000007',
  'b0b0b0b0-0008-4000-a000-000000000008',
];
const _POD_UUIDS = [
  'c0c0c0c0-0001-4000-a000-000000000001',
  'c0c0c0c0-0002-4000-a000-000000000002',
  'c0c0c0c0-0003-4000-a000-000000000003',
  'c0c0c0c0-0004-4000-a000-000000000004',
  'c0c0c0c0-0005-4000-a000-000000000005',
  'c0c0c0c0-0006-4000-a000-000000000006',
  'c0c0c0c0-0007-4000-a000-000000000007',
  'c0c0c0c0-0008-4000-a000-000000000008',
];

function _buildTutoPeos(): any[] {
  const now = new Date().toISOString();
  return processEtapes.map((pe: any, i: number) => {
    const hasControles = pe.controles?.length > 0;
    const podId = hasControles ? _POD_UUIDS[i] : undefined;
    return {
      id: _PEO_UUIDS[i],
      dateDebut: now, dateFin: now,
      poste: { id: 'a0a0a0a0-b1b1-4c2c-d3d3-e4e4e4e4e4e4' }, posteId: 'a0a0a0a0-b1b1-4c2c-d3d3-e4e4e4e4e4e4',
      processEtape: pe, processEtapeId: pe.id,
      podId: podId ?? null,
      pod: podId ? {
        id: podId, isActive: true, version: 1,
        controles: (pe.controles ?? []).map((c: any) => {
          const inst = { ...c };
          // Override spec properties to mark as realized instance
          inst.id = crypto.randomUUID();
          inst.podId = podId;
          inst.parentControl = c.id;
          inst.model = false;
          inst.etat = 'CONFORME';
          inst.createDateTime = now;
          inst.date = now;
          if (c.type === 'QUANTITATIF') {
            inst.valeur1 = String(c.nominal ?? 175);
            inst.valeur2 = String(c.nominal ?? 175);
            inst.moyenne = c.nominal ?? 175;
          }
          if (c.type === 'CNC') {
            inst.choix = c.correctAnswer || 'C';
          }
          inst.enregistrements = [{ saisie: c.type === 'QUANTITATIF' ? String(c.nominal ?? 175) : 'Conforme', valid: true, createDateTime: now }];
          return inst;
        }),
      } : null,
      state: 'DONE', optional: pe.optional ?? false,
      checklist: null, checklistId: null, startUserId: null, endUserId: null,
    };
  });
}

function _buildTutoPoste(etatOverride?: string): any {
  const now = new Date().toISOString();
  const of = ofs[0];
  const isTerminee = etatOverride === 'TERMINEE';
  return {
    id: 'a0a0a0a0-b1b1-4c2c-d3d3-e4e4e4e4e4e4', index: 0, denomination: 'Opé. 1',
    etat: etatOverride ?? 'EN_COURS',
    dateDebut: now, dateFin: isTerminee ? now : null,
    quantitePrevue: of?.quantite ?? 250,
    quantiteProduite: isTerminee ? 250 : 0,
    quantiteProduitePoste: isTerminee ? 250 : 0,
    processesEtapeDone: _buildTutoPeos(),
    changementsLot: [], controles: [], controlesEvenement: [],
    controlesCalages: [], etapesCalages: [], consoLots: [],
    controleConso: [], integration: [], intervals: [],
    podInitial: null, podStart: null, podEnd: null,
    score: isTerminee ? 5 : 0,
    commentaire: isTerminee ? 'Production conforme, bon rendement.' : '',
    commentairePositif: '', commentaireNegatif: '',
    of, ligne: lignes[0] ?? null, userInfos: null, isActive: true, version: 1,
  };
}

function _buildTutoChangementsLot(): any[] {
  const now = new Date().toISOString();
  const posteId = 'a0a0a0a0-b1b1-4c2c-d3d3-e4e4e4e4e4e4';
  return [
    { id: 'cl-farine', ofId: OF_BISCUITS, articleId: ART_FARINE, lotId: LOT_FARINE, lot: { id: LOT_FARINE, numero: 'FAR-2026-001' }, date: now, quantite: null, isStart: true, identifiant: '', posteId, createMouvement: false, takeStock: false, takePDC: false, makeMove: false, mouvementId: null, article: articles[0] },
    { id: 'cl-beurre', ofId: OF_BISCUITS, articleId: ART_BEURRE, lotId: LOT_BEURRE, lot: { id: LOT_BEURRE, numero: 'BEU-2026-001' }, date: now, quantite: null, isStart: true, identifiant: '', posteId, createMouvement: false, takeStock: false, takePDC: false, makeMove: false, mouvementId: null, article: articles[1] },
    { id: 'cl-sucre', ofId: OF_BISCUITS, articleId: ART_SUCRE, lotId: LOT_SUCRE, lot: { id: LOT_SUCRE, numero: 'SUC-2026-001' }, date: now, quantite: null, isStart: true, identifiant: '', posteId, createMouvement: false, takeStock: false, takePDC: false, makeMove: false, mouvementId: null, article: articles[2] },
    { id: 'cl-oeufs', ofId: OF_BISCUITS, articleId: ART_OEUFS, lotId: LOT_OEUFS, lot: { id: LOT_OEUFS, numero: 'OEU-2026-001' }, date: now, quantite: null, isStart: true, identifiant: '', posteId, createMouvement: false, takeStock: false, takePDC: false, makeMove: false, mouvementId: null, article: articles[3] },
    { id: 'cl-lait', ofId: OF_BISCUITS, articleId: ART_LAIT, lotId: LOT_LAIT, lot: { id: LOT_LAIT, numero: 'LAI-2026-001' }, date: now, quantite: 12.5, isStart: true, identifiant: '', posteId, createMouvement: false, takeStock: false, takePDC: false, makeMove: false, mouvementId: null, article: articles[4] },
  ];
}

/**
 * Build realistic bilan matière data for the cloture tutorial.
 * Used by the interceptor to populate the validation dialog with costs and percentages.
 */
export function _buildTutoBilans(quantiteProduite = 250): any[] {
  const posteId = 'a0a0a0a0-b1b1-4c2c-d3d3-e4e4e4e4e4e4';
  // Realistic consumption: slight losses on each ingredient
  const bilansData = [
    { articleId: ART_FARINE, ai: articleInfosFarine, unite: 'Kilogramme', lotId: LOT_FARINE,
      enPdc: 100, consoTheo: 100, consoReel: 98, restante: 2, coutUnit: 0.85, auto: false, perteN: 2, perteM: 5 },
    { articleId: ART_BEURRE, ai: articleInfosBeurre, unite: 'Kilogramme', lotId: LOT_BEURRE,
      enPdc: 62.5, consoTheo: 62.5, consoReel: 63.2, restante: null, coutUnit: 5.40, auto: true, perteN: 1, perteM: 3 },
    { articleId: ART_SUCRE, ai: articleInfosSucre, unite: 'Kilogramme', lotId: LOT_SUCRE,
      enPdc: 50, consoTheo: 50, consoReel: 50, restante: null, coutUnit: 0.72, auto: true, perteN: 1, perteM: 2 },
    { articleId: ART_OEUFS, ai: articleInfosOeufs, unite: 'Unite', lotId: LOT_OEUFS,
      enPdc: 25, consoTheo: 25, consoReel: 25.5, restante: null, coutUnit: 0.15, auto: true, perteN: 2, perteM: 5 },
    { articleId: ART_LAIT, ai: articleInfosLait, unite: 'Litre', lotId: LOT_LAIT,
      enPdc: 12.5, consoTheo: 12.5, consoReel: 12.5, restante: null, coutUnit: 0.95, auto: false, perteN: 1, perteM: 3 },
  ];

  return bilansData.map((b) => {
    const consoTheoriqueUnitaire = b.consoTheo / quantiteProduite;
    const pertesPourcent = b.consoTheo > 0 ? b.consoReel / b.consoTheo : 1;
    const retourPdc = b.enPdc - b.consoReel;
    const pertes = b.consoReel - b.consoTheo;
    return {
      id: `bilan-${b.articleId.substring(0, 8)}`,
      articleId: b.articleId,
      ofId: OF_BISCUITS,
      posteId,
      tiersId: null,
      articleInfos: b.ai,
      uniteDenomination: b.unite,
      enPdc: b.enPdc,
      consoTheoriqueUnitaire,
      consoTheorique: b.consoTheo,
      retourPdc: retourPdc > 0 ? retourPdc : 0,
      consoProd: b.consoReel,
      pertes: pertes > 0 ? pertes : 0,
      pertesPourcent,
      nonConforme: 0,
      coutUnitaire: b.coutUnit,
      coutMaterielProfondUnitaire: b.coutUnit,
      coutMOProfondUnitaire: 0,
      heuresProfondUnitaire: 0,
      auto: b.auto,
      state: Math.abs(pertesPourcent * 100 - 100) <= b.perteN ? 'valid' : 'warn',
      valid: true,
      percentNormal: b.perteN,
      percentMaxi: b.perteM,
      stocks: { lots: [], pdc: [] },
      dataPostes: {},
      dataReinte: {},
      bilanParPoste: [{
        posteId,
        reinte: true,
        consoLots: [{
          id: `lc-${b.articleId.substring(0, 8)}`,
          lotId: b.lotId,
          posteId,
          articleId: b.articleId,
          reinte: true,
          quantiteMiseEnPdc: b.enPdc,
          quantiteInitiale: b.enPdc,
          quantiteRestante: b.restante ?? (b.enPdc - b.consoReel),
          quantiteConsommee: b.consoReel,
          lot: lots.find((l: any) => l.id === b.lotId),
        }],
      }],
    };
  });
}

/**
 * Build a realistic BilanProductivite for the validation dialog.
 * Based on 250 kg produced, 2 operators, cadence 50 u/h.
 */
export function _buildTutoBilanProductivite(): any {
  const qtt = 250;
  const cadence = 50;
  const nbPersonnes = 2;
  const tempsCalage = 15; // min
  const tempsNettoyage = 20; // min
  const heuresTheorique = parseFloat(((qtt / cadence + tempsCalage / 60 + tempsNettoyage / 60) * nbPersonnes).toFixed(2));
  const heuresTravailles = 11.5;
  const salaireMoyen = 18;
  const productiviteTheorique = parseFloat((qtt / heuresTheorique).toFixed(1));
  const productiviteReel = parseFloat((qtt / heuresTravailles).toFixed(1));
  const productivitePourcent = parseFloat(((productiviteReel / productiviteTheorique) * 100).toFixed(1));
  const coutTotalTheorique = parseFloat((heuresTheorique * salaireMoyen).toFixed(2));
  const coutTotalPersonnel = parseFloat((heuresTravailles * salaireMoyen).toFixed(2));

  return {
    id: 'bp-tuto-001',
    cadence,
    nbPersonnes,
    tempsCalage,
    tempsNettoyage,
    heuresLigne: parseFloat((heuresTravailles / nbPersonnes).toFixed(2)),
    heuresTheorique,
    heuresTravailles,
    ecartHeuresTravailles: parseFloat((heuresTravailles - heuresTheorique).toFixed(2)),
    coutTotalPersonnel,
    coutTotalTheorique,
    ecartCoutTheorique: parseFloat(((coutTotalPersonnel / coutTotalTheorique - 1) * 100).toFixed(1)),
    ecartCoutTheoriquePourcent: parseFloat(((coutTotalPersonnel / coutTotalTheorique - 1) * 100).toFixed(1)),
    productiviteTheorique,
    productiviteReel,
    productivitePourcent,
    ecartProductiviteTheorique: parseFloat(((productiviteReel / productiviteTheorique - 1) * 100).toFixed(1)),
    qttProduite: qtt,
    salaireMoyen,
    surcoutTotal: parseFloat((coutTotalPersonnel - coutTotalTheorique).toFixed(2)),
    surcoutUnitaire: parseFloat(((coutTotalPersonnel - coutTotalTheorique) / qtt).toFixed(4)),
    surcoutUnitairePourcent: parseFloat(((coutTotalPersonnel / coutTotalTheorique - 1) * 100).toFixed(1)),
    coutUnitaireReel: parseFloat((coutTotalPersonnel / qtt).toFixed(4)),
    coutUnitaireTheorique: parseFloat((coutTotalTheorique / qtt).toFixed(4)),
    isActive: true, version: 1,
  };
}

/**
 * Build mock Travail entries for the hours display in the validation dialog.
 */
export function _buildTutoTravaux(): any[] {
  const now = new Date().toISOString().split('T')[0];
  return [
    { id: 'trav-001', ofId: OF_BISCUITS, ofNumero: 'OF2600001', userInfosId: 'ui-pierre', userInfos: { id: 'ui-pierre', nom: 'NICOLET', prenom: 'Pierre' }, date: now, duree: '06:00', defaultType: 'OF', isActive: true, version: 1 },
    { id: 'trav-002', ofId: OF_BISCUITS, ofNumero: 'OF2600001', userInfosId: 'ui-marie', userInfos: { id: 'ui-marie', nom: 'DUPONT', prenom: 'Marie' }, date: now, duree: '05:30', defaultType: 'OF', isActive: true, version: 1 },
  ];
}

/**
 * Build the Sucre cristal order line for CF003.
 * Used by the tutorial panel to inject the line after the "add ligne" step.
 */
export function _buildTutoCommandeLigneSucre(): any {
  return makeCfLigne('cfl-tuto-sucre', ART_SUCRE, 250, 0.72, 'KG', 'SUC-CRIST-25', CF_003, 0);
}

/**
 * Flat map of all demo entities by type, for direct programmatic access.
 */
export const TUTORIAL_DEMO_DATA = {
  articles,
  articleInfos: articleInfosList,
  specificationMasters,
  tiers,
  adresses,
  lots,
  commandesFournisseur,
  commandesClient,
  customEnums,
  nomenclatures,
  nomenclatureLignes,
  processEtapes,
  controles,
  controlePod,
  controlePods,
  lignes,
  ofs,
  activityInfos,
} as const;

// ---------------------------------------------------------------------------
// Mock route registry
// ---------------------------------------------------------------------------

const UUID = '[0-9a-z-]{36}';

/**
 * Registry of mock GET responses keyed by URL pattern.
 * Evaluated top-to-bottom; first match wins.
 */
// ---------------------------------------------------------------------------
// Progress-aware helpers for SEM/SE responses
// Progress format: moduleOrder + globalStepIndex / 100 (e.g., 6.11)
// ---------------------------------------------------------------------------

/** Returns a copy of the SE (version) with progress-aware nomenclature + process */
function _progressAwareSE(se: any, p: number): any {
  if (!se) return se;
  const copy = { ...se };

  // Nomenclature: empty → 1 line → complete
  // order=7: save at global 11 (7.11), all lines after ajout-done at global 15 (7.15)
  if (copy.nomenclature) {
    if (p >= 7.15) {
      // keep full (after nom-ajout-done reloadAfter)
    } else if (p >= 7.11) {
      copy.nomenclature = { ...copy.nomenclature, nomenclatureLignes: copy.nomenclature.nomenclatureLignes?.slice(0, 1) ?? [] };
    } else {
      copy.nomenclature = { ...copy.nomenclature, nomenclatureLignes: [] };
    }
  }

  // Process: empty → 1 step → complete (with controles)
  if (copy.process?.length) {
    if (p >= 7.11) {
      // Enrich process steps with their pod controles, filtered by availableAt
      copy.process = copy.process.map((pe: any) => {
        if (!pe.podId) return pe;
        const pod = controlePods.find((cp: any) => cp.id === pe.podId);
        if (!pod) return pe;
        const filteredControles = pod.controles
          .filter((c: any) => !c.availableAt || p >= c.availableAt)
          .map((c: any) => _enrichControleIfDone(c, p));
        return filteredControles.length > 0
          ? { ...pe, pod: { ...pod, controles: filteredControles }, controles: filteredControles }
          : pe;
      });
    } else if (p >= 7.10) {
      copy.process = copy.process.slice(0, 1);
    } else {
      copy.process = [];
    }
  }

  return copy;
}

/** Returns a copy of the SEM with progress-aware versions */
function _progressAwareSEM(sem: any, p: number): any {
  if (!sem) return sem;
  return {
    ...sem,
    versions: sem.versions?.map((v: any) => _progressAwareSE(v, p)) ?? [],
  };
}

/** After gestion-production (order 13) is done, controls should appear filled.
 *  Module gestion-traca-production is order 14 → p >= 14 means production controls done. */
function _enrichControleIfDone(c: any, p: number): any {
  if (p < 14) return c;
  const filled: any = { ...c, etat: 'CONFORME', date: _now.toISOString() };
  if (c.type === 'QUANTITATIF') {
    filled.valeur1 = String(c.nominal || 175);
    filled.valeur2 = String(c.nominal || 175);
    filled.moyenne = c.nominal || 175;
  }
  if (c.type === 'CNC') {
    filled.choix = c.correctAnswer || 'C';
  }
  return filled;
}

/** Returns OFs with progression-aware state for OF1 */
function _getOfsWithState(p: number): any[] {
  return ofs
    .filter((o: any) => !o.availableAt || p >= o.availableAt)
    .map((o: any) => {
      if (o.id !== OF_BISCUITS) return o;
      // OF1 state evolves with tutorial progression
      // Module 15 (clôture) starts with EN_PROD — user triggers end-prod during the tuto
      if (p >= 13) return { ...o, etat: 'EN_PROD', started: true, quantiteProduite: p >= 15 ? 250 : 0 };
      return o;
    });
}

const MOCK_ROUTES: MockRouteEntry[] = [
  // --- tutorials/progress (the tutorial system itself) ---
  { pattern: /\/api\/tutorials\/progress$/,       data: [] },

  // --- Articles ---
  { pattern: /\/api\/articles\/?$/,
    data: (_m: RegExpMatchArray, p: number) => {
      if (p >= 4.20) return articles;
      if (p >= 4.10 || p >= 6.11) return articles.filter(a => a.id !== ART_BISCUITS);
      // No progress: exclude Lait and Biscuits
      return articles.filter(a => a.id !== ART_LAIT && a.id !== ART_BISCUITS);
    },
  },
  { pattern: new RegExp(`/api/articles/id/(${UUID})$`),
    data: (m: RegExpMatchArray) => articles.find(a => a.id === m[1]) ?? null },

  // --- SpecificationEtapeMaster ---
  { pattern: new RegExp(`/api/specification_etape/master/article/(${UUID})$`),
    data: (m: RegExpMatchArray, p: number) => {
      const sem = specificationMasters.find(s => s.articleId === m[1]);
      return sem ? _progressAwareSEM(sem, p) : null;
    }},
  { pattern: new RegExp(`/api/specification_etape/master/(${UUID})$`),
    data: (m: RegExpMatchArray, p: number) => {
      const sem = specificationMasters.find(s => s.id === m[1]);
      return sem ? _progressAwareSEM(sem, p) : null;
    }},
  { pattern: new RegExp(`/api/specification_etape/id/(${UUID})/eager/parents$`),
    data: (m: RegExpMatchArray, p: number) => {
      const sem = specificationMasters.find(s => s.id === m[1]);
      if (!sem) return null;
      // Return a copy with progress-aware nomenclature/process on the active version
      return _progressAwareSEM(sem, p);
    }},
  { pattern: new RegExp(`/api/specification_etape/id/(${UUID})/infos$`),
    data: (m: RegExpMatchArray, p: number) => {
      for (const sem of specificationMasters) {
        const v = sem.versions?.find((v: any) => v.id === m[1]);
        if (v) {
          return _progressAwareSE(v, p);
        }
      }
      return null;
    }},
  { pattern: /\/api\/specification_etape\/master\b/,
    data: (_m: RegExpMatchArray, p: number) => {
      if (p >= 4.20) return specificationMasters;
      if (p >= 4.10 || p >= 6.11) return specificationMasters.filter(s => s.articleId !== ART_BISCUITS);
      // No progress: exclude Lait and Biscuits
      return specificationMasters.filter(s => s.articleId !== ART_LAIT && s.articleId !== ART_BISCUITS);
    },
  },

  // --- ArticleInfos ---
  { pattern: /\/api\/specification_etape\/infosArticle\/?$/,
    data: (_m: RegExpMatchArray, p: number) => {
      if (p >= 4.20) return articleInfosList;
      if (p >= 4.10 || p >= 6.11) return articleInfosList.filter(ai => ai.articleId !== ART_BISCUITS);
      return articleInfosList.filter(ai => ai.articleId !== ART_LAIT && ai.articleId !== ART_BISCUITS);
    },
  },
  { pattern: new RegExp(`/api/specification_etape/infosArticle/articleId/(${UUID})$`),
    data: (m: RegExpMatchArray) => articleInfosList.find(ai => ai.articleId === m[1]) ?? null },

  // --- Tiers ---
  { pattern: /\/api\/tiers\/?$/,                  data: tiers },
  { pattern: new RegExp(`/api/tiers/(${UUID})$`),
    data: (m: RegExpMatchArray) => tiers.find(t => t.id === m[1]) ?? null },

  // --- Adresses ---
  { pattern: /\/api\/adresse\/?$/,                data: adresses },
  { pattern: new RegExp(`/api/adresse/id/(${UUID})$`),
    data: (m: RegExpMatchArray) => adresses.find(a => a.id === m[1]) ?? null },
  { pattern: new RegExp(`/api/adresse/(${UUID})$`),
    data: (m: RegExpMatchArray) => adresses.filter(a => a.tiersId === m[1]) },

  // --- Lots ---
  { pattern: new RegExp(`/api/lots/article/(${UUID})/light_stock$`),
    data: (m: RegExpMatchArray) => lots.filter(l => l.articleId === m[1]).map(l => ({
      ...l,
      stocks: [
        { id: 'stk-pdc-' + l.id, lotId: l.id, quantite: l.quantite, adresseId: 'adr-pdc-001', adresse: { id: 'adr-pdc-001', denomination: 'PDC Ligne Biscuits' } },
        { id: 'stk-ligne-' + l.id, lotId: l.id, quantite: l.quantite, adresseId: ADR_MOULIN, adresse: adresses[0] },
      ],
    })),
  },
  { pattern: new RegExp(`/api/lots/article/(${UUID})/light$`),
    data: (m: RegExpMatchArray) => lots.filter(l => l.articleId === m[1]) },
  { pattern: new RegExp(`/api/lots/article/(${UUID})$`),
    data: (m: RegExpMatchArray) => lots.filter(l => l.articleId === m[1]) },
  { pattern: new RegExp(`/api/lots/id/(${UUID})$`),
    data: (m: RegExpMatchArray) => lots.find(l => l.id === m[1]) ?? null },

  // --- Commandes fournisseur ---
  { pattern: /\/api\/commandes\/?$/,              data: (_m: RegExpMatchArray, p: number) => commandesFournisseur.filter((c: any) => !c.availableAt || p >= c.availableAt) },
  { pattern: /\/api\/commandes\/navfilter\b/,     data: (_m: RegExpMatchArray, p: number) => commandesFournisseur.filter((c: any) => !c.availableAt || p >= c.availableAt) },
  { pattern: new RegExp(`/api/commandes/eager/(${UUID})$`),
    data: (m: RegExpMatchArray) => commandesFournisseur.find(c => c.id === m[1]) ?? null },

  // --- Commandes client ---
  { pattern: /\/api\/commandes_client\/?$/,       data: commandesClient },
  { pattern: /\/api\/commandes_client\/navfilter\b/, data: commandesClient },
  { pattern: new RegExp(`/api/commandes_client/(${UUID})$`),
    data: (m: RegExpMatchArray) => commandesClient.find(c => c.id === m[1]) ?? null },
  { pattern: new RegExp(`/api/commandes_client/eager/(${UUID})$`),
    data: (m: RegExpMatchArray) => commandesClient.find(c => c.id === m[1]) ?? null },

  // --- Custom enums ---
  { pattern: /\/api\/custom_enum\/?$/,            data: (_m: RegExpMatchArray, p: number) => customEnums.filter((e: any) => !e.availableAt || p >= e.availableAt) },
  { pattern: /\/api\/custom_enum\/(FAMILLE_ARTICLE|CATEGORIE_ARTICLE|SOUS_FAMILLE_ARTICLE|FAMILLE_TECHNIQUE|UNITE|UNITE_PROD|EMPLACEMENT_STOCK|POSTE|TITRE|TRAVAIL|TYPE_REGLEMENT|NC_NATURE)$/,
    data: (m: RegExpMatchArray, p: number) => customEnums.filter((e: any) => e.type === m[1] && (!e.availableAt || p >= e.availableAt)) },

  // --- Commande lignes ---
  { pattern: new RegExp(`/api/commandes/ligne/commandeId/(${UUID})$`),
    data: (m: RegExpMatchArray) => {
      const cf = commandesFournisseur.find(c => c.id === m[1]);
      return cf?.lignes ?? [];
    },
  },

  // --- Commande client lignes ---
  { pattern: new RegExp(`/api/commandes_client/ligne/commandeClientId/(${UUID})$`),
    data: (m: RegExpMatchArray) => {
      const cc = commandesClient.find(c => c.id === m[1]);
      return cc?.lignes ?? [];
    },
  },

  // --- Expéditions ---
  { pattern: /\/api\/expedition\b/, data: [] },
  { pattern: /\/api\/palette\b/, data: [] },
  { pattern: /\/api\/chargement\b/, data: [] },

  // --- Bons de livraison ---
  { pattern: /\/api\/bon_livraison\b/, data: [] },
  { pattern: /\/api\/bon_livraison_fournisseur\b/, data: [] },

  // --- Factures ---
  { pattern: /\/api\/facture\b/, data: [] },
  { pattern: /\/api\/facture_fournisseur\b/, data: [] },
  { pattern: /\/api\/avoir\b/, data: [] },

  // --- Transferts ---
  { pattern: /\/api\/transfert\b/, data: [] },

  // --- Inventaire ---
  { pattern: /\/api\/inventaire\b/, data: [] },

  // --- Tracabilité ---
  { pattern: /\/api\/tracabilite\b/, data: [] },
  { pattern: /\/api\/sankey\b/, data: [] },

  // --- Article Fourni ---
  { pattern: new RegExp(`/api/article_fourni/tiers/(${UUID})$`), data: (m: RegExpMatchArray, p: number) => {
    // After section 2 (parametrage article achat), return Sucre article fourni for Moulin de Provence
    if (m[1] === TIERS_MOULIN && p >= 15.18) {
      return [{
        id: 'af-tuto-sucre', forClient: false, reference: 'PROV-SUC-001',
        articleId: ART_SUCRE, tiersId: TIERS_MOULIN, tierGroupId: null,
        uniteGestion: 'sac 25kg', coefficientUniteGestion: 25, tva: 5.5,
        multipleAchat: null, multipleVente: null, taxRateId: null, userId: null,
        article: articles[2], articleInfos: articles[2]?.articleInfos,
        tiers: tiers[0], tierGroup: null,
        infosPrix: [{ id: 'ip-tuto-sucre', isPrixAchat: true, quantiteMin: 1, prix: 18, delaiLivraison: 3, articleFourniId: 'af-tuto-sucre' }],
        isActive: true, version: 1,
      }];
    }
    return [];
  }},
  { pattern: new RegExp(`/api/article_fourni/tiers/$`), data: [] },
  { pattern: new RegExp(`/api/article_fourni/article/(${UUID})$`), data: [] },
  { pattern: new RegExp(`/api/article_fourni/tier-group/(${UUID})$`), data: [] },

  // --- Remises ---
  { pattern: /\/api\/remise\/search/, data: [] },
  { pattern: new RegExp(`/api/remise/tiers/(${UUID})$`), data: [] },

  // --- Tax Rates ---
  { pattern: /\/api\/tax_rate\b/, data: [] },

  // --- Tiers ---
  { pattern: /\/api\/tiers\/?$/, data: tiers },
  { pattern: /\/api\/tiers\/navfilter\b/, data: tiers },
  { pattern: new RegExp(`/api/tiers/id/(${UUID})$`),
    data: (m: RegExpMatchArray) => tiers.find(t => t.id === m[1]) ?? null },
  { pattern: new RegExp(`/api/tiers/(${UUID})/analytics-summary$`),
    data: () => ({ hasSales: false, hasPurchases: false, hasTransport: false }) },

  // --- Custom enums by type ---
  { pattern: /\/api\/custom_enum\/type\/([A-Z_]+)$/,
    data: (m: RegExpMatchArray, p: number) => customEnums.filter((e: any) => e.type === m[1] && (!e.availableAt || p >= e.availableAt)) },

  // --- Nomenclatures ---
  { pattern: /\/api\/nomenclature\/?$/,             data: nomenclatures },
  { pattern: new RegExp(`/api/nomenclature/(${UUID})$`),
    data: (m: RegExpMatchArray) => nomenclatures.find(n => n.id === m[1]) ?? null },
  { pattern: new RegExp(`/api/specification_etape/nomenclature/(${UUID})$`),
    data: (m: RegExpMatchArray, p: number) => {
      const nom = nomenclatures.find(n => n.specificationEtapeId === m[1]);
      if (!nom) return null;
      if (p >= 7.15) return nom;
      if (p >= 7.11) {
        return { ...nom, nomenclatureLignes: nom.nomenclatureLignes.slice(0, 1) };
      }
      // No progress: empty nomenclature lines
      return { ...nom, nomenclatureLignes: [] };
    },
  },

  // --- Process Etapes ---
  { pattern: /\/api\/specification_etape\/process\/?$/,  data: processEtapes },
  { pattern: new RegExp(`/api/specification_etape/process/(${UUID})$`),
    data: (m: RegExpMatchArray, p: number) => {
      const filtered = processEtapes.filter(pe => pe.specificationEtapeId === m[1]);
      if (filtered.length === 0) return filtered;
      if (p >= 7.11) return filtered;
      if (p >= 7.10) return filtered.slice(0, 1);
      // No progress: empty process
      return [];
    },
  },

  // --- Controles ---
  { pattern: /\/api\/controle\/?$/,
    data: (_m: RegExpMatchArray, p: number) => controles
      .filter((c: any) => !c.availableAt || p >= c.availableAt)
      .map((c: any) => _enrichControleIfDone(c, p)) },
  { pattern: new RegExp(`/api/controle/(${UUID})$`),
    data: (m: RegExpMatchArray, p: number) => {
      const c = controles.find(c => c.id === m[1]);
      return c ? _enrichControleIfDone(c, p) : null;
    } },
  { pattern: /\/api\/controle\/general\b/,           data: [] },

  // --- Lignes de production ---
  { pattern: /\/api\/lignes\/eager\/?$/,             data: (_m: RegExpMatchArray, p: number) => lignes.filter((l: any) => !l.availableAt || p >= l.availableAt) },
  { pattern: /\/api\/lignes\/?$/,                    data: (_m: RegExpMatchArray, p: number) => lignes.filter((l: any) => !l.availableAt || p >= l.availableAt) },
  { pattern: new RegExp(`/api/lignes/id/(${UUID})$`),
    data: (m: RegExpMatchArray, p: number) => {
      const l = lignes.find(l => l.id === m[1]);
      if (!l || (l.availableAt && p < l.availableAt)) return null;
      return l;
    }},

  // --- OF (Ordres de Fabrication) ---
  { pattern: /\/api\/of\/nav\b/,                     data: (_m: RegExpMatchArray, p: number) => _getOfsWithState(p) },
  { pattern: /\/api\/of\/navfilter\b/,               data: (_m: RegExpMatchArray, p: number) => _getOfsWithState(p) },
  { pattern: new RegExp(`/api/of/id/eager/(${UUID})$`),
    data: (m: RegExpMatchArray) => ofs.find(o => o.id === m[1]) ?? null },
  { pattern: /\/api\/of\/for_selection\b/,           data: (_m: RegExpMatchArray, p: number) => ofs.filter((o: any) => !o.availableAt || p >= o.availableAt) },
  { pattern: /\/api\/of\/to\/plannif\b/,             data: (_m: RegExpMatchArray, p: number) => ofs.filter((o: any) => (!o.availableAt || p >= o.availableAt) && o.etat === 'EN_ATTENTE_PLANNIF') },

  // --- Pied de Chaîne (production supply chain) ---
  // Structure alignée sur PiedDeChaineService.buildForOf() côté back
  { pattern: new RegExp(`/api/of/pdc/(${UUID})$`),
    data: (m: RegExpMatchArray, p: number) => {
      const of = ofs.find((o: any) => o.id === m[1]);
      if (!of) return null;
      // articlesLots : 1 entrée par ligne de nomenclature
      const artLots = nomenclatureLignes.map((nl: any) => {
        const art = articles.find((a: any) => a.id === nl.articleId);
        const ai = art?.articleInfos;
        const artLots = lots.filter((l: any) => l.articleId === nl.articleId)
          .map((l: any) => ({
            ...l,
            stocks: [{ id: 'stk-' + l.id, lotId: l.id, quantite: l.quantite, adresseId: 'adr-pdc-001', adresse: { id: 'adr-pdc-001', denomination: 'PDC Ligne Biscuits' } }],
          }));
        const semId = art?.specificationEtapeMasterId;
        const pdc = ai?.withPiedDeChaine
          ? artLots.map((l: any) => ({ ...l, stocks: [{ id: 'stk-pdc-' + l.id, lotId: l.id, quantite: l.quantite, adresseId: 'adr-pdc-001', adresse: { id: 'adr-pdc-001', denomination: 'PDC Ligne Biscuits' } }] }))
          : [];
        return {
          articleInfos: ai,
          articleId: nl.articleId,
          lots: artLots,
          pdc,
          tiersId: null,
          specificationEtapeRefId: semId ? (semId + '-se') : null,
        };
      });
      return {
        adresse: { id: 'adr-pdc-001', denomination: 'Pied de chaîne - Ligne Biscuits' },
        lot: lots.find((l: any) => l.articleId === ART_BISCUITS),
        articlesLots: artLots,
        nomenclature: { nomenclatureLignes },
        path: {
          id: 'path-biscuits',
          quantityProdDefault: 250,
          processEtapes,
          nomenclatureLignes,
          articleInfos: articleInfosBiscuits,
          podControlesProductionIds: [],
          podControlesCalageIds: [],
          podControlesFinIds: [],
          podControlesStartOpeIds: [],
          podControlesEndOpeIds: [],
          process: [{ process: processEtapes }],
          checklists: [],
        },
        of: (() => {
          // For traca module (progress >= 14), include a poste with all PEOs done
          if (p >= 14) {
            const lotBiscuits = lots.find((l: any) => l.articleId === ART_BISCUITS);
            const now = new Date().toISOString();
            const tutoMouvements = [
              { id: 'mvt-pdc-farine', type: 'PIED_DE_CHAINE', articleId: ART_FARINE, lotId: LOT_FARINE, quantite: 100, date: now, ofId: OF_BISCUITS, lot: lots[0], articleInfos: articleInfosFarine },
              { id: 'mvt-pdc-lait', type: 'PIED_DE_CHAINE', articleId: ART_LAIT, lotId: LOT_LAIT, quantite: 12.5, date: now, ofId: OF_BISCUITS, lot: lots[4], articleInfos: articleInfosLait },
            ];
            // OF stays EN_PROD — user triggers end-prod during cloture tuto
            const isCloture = p >= 15;
            const ofEtat = 'EN_PROD';
            const poste = isCloture ? _buildTutoPoste('TERMINEE') : _buildTutoPoste();
            return { ...of, etat: ofEtat, started: true, withQuantiteOpe: true, quantiteProduite: isCloture ? 250 : 0, qttReinteTemp: isCloture ? 250 : 0, postes: [poste], integrations: [], bilans: [], lots: lotBiscuits ? [lotBiscuits] : [], mouvements: tutoMouvements };
          }
          return { ...of, postes: of.postes ?? [], integrations: of.integrations ?? [], bilans: of.bilans ?? [] };
        })(),
      };
    },
  },

  // --- Travail (Hours) ---
  { pattern: new RegExp(`/api/travail/of/(${UUID})$`), data: (_m: RegExpMatchArray, p: number) => p >= 15 ? _buildTutoTravaux() : [] },

  // --- Poste (Operations) ---
  { pattern: /\/api\/poste\/?$/, data: [] },
  { pattern: new RegExp(`/api/poste/of/(${UUID})$`), data: [] },
  // poste/eager — returns the tuto poste when progress >= 14 (traca module)
  { pattern: new RegExp(`/api/poste/eager/(${UUID})$`), data: (_m: RegExpMatchArray, p: number) => {
    if (p < 14) return null;
    const poste = _buildTutoPoste(p >= 15 ? 'TERMINEE' : undefined);
    // Deep-enrich: mark spec controles inside process steps as CONFORME
    // so the UI (which reads spec controles, not PEO controles) shows them filled.
    if (p >= 14 && poste.processesEtapeDone) {
      for (const peo of poste.processesEtapeDone) {
        const pe = peo.processEtape;
        if (pe?.controles) {
          pe.controles = pe.controles.map((c: any) => _enrichControleIfDone(c, p));
        }
        if (pe?.pod?.controles) {
          pe.pod.controles = pe.pod.controles.map((c: any) => _enrichControleIfDone(c, p));
        }
      }
    }
    return poste;
  }},
  { pattern: new RegExp(`/api/poste/changements/(${UUID})$`), data: (_m: RegExpMatchArray, p: number) => {
    if (p >= 14) return _buildTutoChangementsLot();
    return [];
  }},
  { pattern: new RegExp(`/api/poste/changements/posteId/(${UUID})$`), data: (_m: RegExpMatchArray, p: number) => {
    if (p >= 14) return _buildTutoChangementsLot();
    return [];
  }},

  // --- Tempo (intervals de contrôle) ---
  { pattern: new RegExp(`/api/tempo/bundles/posteIntervals/posteId/(${UUID})$`), data: [] },

  // --- PEO (ProcessEtapeOpe) ---
  { pattern: new RegExp(`/api/peo/id/(${UUID})$`), data: () => null },
  { pattern: /\/api\/peo\/search\b/, data: (_m: RegExpMatchArray, p: number) => {
    if (p >= 14) return _buildTutoPeos();
    return [];
  }},

  // --- Controles ---
  { pattern: new RegExp(`/api/controle/podId/(${UUID})$`),
    data: (m: RegExpMatchArray, p: number) => {
      const pod = controlePods.find((cp: any) => cp.id === m[1]);
      if (!pod) return [];
      return pod.controles
        .filter((c: any) => !c.availableAt || p >= c.availableAt)
        .map((c: any) => _enrichControleIfDone(c, p));
    },
  },

  // --- Lots par article ---
  { pattern: new RegExp(`/api/lots/by_article/(${UUID})$`),
    data: (m: RegExpMatchArray) => lots.filter((l: any) => l.articleId === m[1]),
  },

  // --- Adresses ---
  { pattern: /\/api\/adresse\b/, data: [{ id: 'adr-pdc-001', denomination: 'PDC Ligne Biscuits', isActive: true }] },

  // --- Checklists ---
  { pattern: new RegExp(`/api/checklist/for_of/(${UUID})$`), data: [] },

  // --- Mouvements ---
  { pattern: /\/api\/lot_mouvements\b/, data: (_m: RegExpMatchArray, p: number) => {
    if (p >= 14) {
      const now = new Date().toISOString();
      return [
        { id: 'mvt-pdc-farine', type: 'PIED_DE_CHAINE', articleId: ART_FARINE, lotId: LOT_FARINE, quantite: 100, date: now, ofId: OF_BISCUITS, lot: lots[0], articleInfos: articleInfosFarine },
        { id: 'mvt-pdc-lait', type: 'PIED_DE_CHAINE', articleId: ART_LAIT, lotId: LOT_LAIT, quantite: 0.05, date: now, ofId: OF_BISCUITS, lot: lots[4], articleInfos: articleInfosLait },
      ];
    }
    return [];
  }},

  // --- Integration PF ---
  { pattern: /\/api\/integrations\b/, data: [] },

  // --- Stock outdated ---
  { pattern: new RegExp(`/api/of/stock_outdated/(${UUID})$`), data: [] },

  // --- Production Navigation ---
  { pattern: /\/api\/of\/prod\/fast-nav\b/, data: (_m: RegExpMatchArray, p: number) => {
    return ofs
      .filter((o: any) => (!o.availableAt || p >= o.availableAt) && (o.etat === 'EN_PROD' || o.etat === 'EN_ATTENTE_PROD'))
      .map((o: any) => ({ fo: o, doc: null }));
  }},

  // --- Stock ---
  { pattern: new RegExp(`/api/stock/lot/(${UUID})$`),
    data: (m: RegExpMatchArray) => {
      const lot = lots.find((l: any) => l.id === m[1]);
      if (!lot) return [];
      return [{ id: 'stk-' + lot.id, lotId: lot.id, quantite: lot.quantite, adresseId: 'adr-pdc-001' }];
    },
  },
  { pattern: /\/api\/stock\b/, data: [] },

  // --- Horaires ---
  { pattern: new RegExp(`/api/horaires/ligne/(${UUID})$`),
    data: () => [{
      id: 'hor-ligne-bisc',
      ligneId: LIGNE_BISC,
      lastChangedDateTime: new Date('2026-01-01').toISOString(),
      lundi: ['09:00-17:00'],
      mardi: ['09:00-17:00'],
      mercredi: ['09:00-17:00'],
      jeudi: ['09:00-17:00'],
      vendredi: ['09:00-17:00'],
      samedi: [],
      dimanche: [],
    }],
  },

  // --- Créneaux (planification Gantt) ---
  { pattern: /\/api\/creneau\b/,
    data: (_m: RegExpMatchArray, p: number) => {
      // Generate creneaux from planned OFs (those with dateDebut + dateFin and visible at current progress)
      return ofs
        .filter((o: any) => o.dateFin && (!o.availableAt || p >= o.availableAt))
        .map((o: any) => ({
          id: 'cr-' + o.id,
          start: o.dateDebut,
          end: o.dateFin,
          ligneId: o.ligneId,
          ofId: o.id,
          ofNumero: o.numero,
          ofArticle: o.articleInfos?.denomination,
          of: o,
          type: 'PLANIFICATION',
          coefPerformance: 1,
          horairesFixes: false,
        }));
    },
  },

  // --- Controles / Pods ---
  { pattern: new RegExp(`/api/controle_pod/id/(${UUID})$`),
    data: (m: RegExpMatchArray, p: number) => {
      const pod = controlePods.find((cp: any) => cp.id === m[1]);
      if (!pod) return null;
      return { ...pod, controles: pod.controles
        .filter((c: any) => !c.availableAt || p >= c.availableAt)
        .map((c: any) => _enrichControleIfDone(c, p))
      };
    },
  },
  { pattern: /\/api\/controle\/general\b/,          data: [] },

  // --- Specif Modifs (demandes de modification) ---
  { pattern: /\/api\/specif_modifs\/semId\//,       data: [] },

  // --- Specification Variable ---
  { pattern: /\/api\/specification-variable\//,     data: [] },

  // --- Activity Infos (entreprise) ---
  { pattern: /\/api\/activity_infos\/?$/,           data: activityInfos },

  // --- Path model ---
  { pattern: /\/api\/specification_etape\/path\//,  data: [] },
  { pattern: /\/api\/specification_etape\/models/,  data: [] },

  // --- Catch-all for write operations in demo mode (return empty success) ---
  // POST/PUT/DELETE endpoints get an empty 200 from the interceptor itself;
  // this file only provides GET data.
];

/**
 * Looks up mock data for a given URL.
 * @param url The request URL to match.
 * @param progress Optional progress value for progressive data.
 * @returns the mock response body, or `null` if no mock is registered.
 */
export function getDemoResponse(url: string, progress?: number): unknown | null {
  // Strip query string for matching
  const pathname = url.split('?')[0];
  const p = progress ?? 0;

  for (const route of MOCK_ROUTES) {
    const match = pathname.match(route.pattern);
    if (match) {
      return typeof route.data === 'function' ? route.data(match, p) : route.data;
    }
  }
  return null;
}
